import { pipelineCapacityDisplay } from '../../lib/pipeline-capacity';
import { pendingFemaLabel, pendingFemaLabels, pendingFloodLayers, PENDING_FEMA_COLOR } from '../../lib/fema-pending';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { REPORT } from '../print/PrintAtoms';
import { PORTFOLIO_TOKENS, toneStyle } from './tokens';
import { getMapboxToken } from '../../lib/config';
import { designTokens } from '../../styles/design-tokens';
import { useMapMeasurement } from '../../hooks/useMapMeasurement';
import type { PortfolioRow, PortfolioPad } from '../../lib/portfolioApi';
import { focusBoundsForPad, geometryBounds as geomBounds } from './crownPineFocusBounds';

type MapStyleMode = 'light' | 'satellite';
const STYLE_URLS: Record<MapStyleMode, string> = {
  light: 'mapbox://styles/mapbox/light-v11',
  satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
};

interface Props {
  rows: PortfolioRow[];
  cycleBbox: [number, number, number, number] | null;
  /** Dot click — typically setActiveParcelId in the parent. Does NOT navigate. */
  onSelect?: (parcelId: string) => void;
  /** Dot hover — typically setHoveredParcelId in the parent for row sync. */
  onHover?: (parcelId: string | null) => void;
  height?: number | string;
  stuckHeight?: number;
  sticky?: boolean;
  /** External active selection — drives the accent-ring layer + flyTo. */
  activeParcelId?: string | null;
  /** Portfolio share-link token. When present, sub-fetches (pipelines overlay)
      authenticate via ?token= instead of localStorage Bearer. */
  shareToken?: string;
  /** Crown Pine research mode — exposes the "All CP" owner-parcel layer + a
      slope-shading toggle (fetched from /api/crownpine-parcels). Off for the
      Integra portfolio, which never passes it, so its map is unchanged. */
  ownerParcels?: boolean;
  /** Crown Pine research mode — the per-pad breakdown. When present, the
      clean-pad layer draws one INDEPENDENT feature per pad (each clickable /
      hoverable), and pad selection drives its own highlight + flyTo. Integra
      never passes it, so its clean-pad layer stays the per-block MultiPolygon. */
  pads?: PortfolioPad[];
  /** Externally-driven active PAD — highlights that pad (no zoom; see focusPadId). */
  activePadId?: string | null;
  /** Pad to ZOOM to — set only when a pad's side panel is opened, so mere
      selection just highlights and the map doesn't lurch while scanning. */
  focusPadId?: string | null;
  /** Row-hover sync for pads (highlights the hovered pad's outline). */
  hoveredPadId?: string | null;
  /** Pad click — typically setActivePadId in the parent. */
  onSelectPad?: (padId: string) => void;
  /** Pad hover — typically setHoveredPadId in the parent for row sync. */
  onHoverPad?: (padId: string | null) => void;
  /** Client presentation — repaints the always-on gas pipeline layer by operator
      family: Energy Transfer in the main map's gas-transmission color and one
      step wider, Kinder Morgan quieter and thinner, every other operator
      transparent. Off (undefined) leaves the research page's neutral lines. */
  pipelineFamilyStyling?: boolean;
  /** The ACTIVE site's screened campus-to-tap connector, split by whether the
      route runs on supplied Crown Pine inventory. Absent / null draws nothing. */
  connector?: ConnectorSegments | null;
  /** Client presentation — draw each pad's `shortlist.buildable.pieces` as the
      highlighted acreage layer instead of the single screened-campus polygon,
      and move that campus polygon to an off-by-default outline row. Every piece
      carries its parent pad id, so hover / click / highlight behave as before.
      Off (undefined) leaves the research page's campus-polygon rendering. */
  buildableHighlight?: boolean;
  /** Initial state of the Crown Pine LRM tract layer. Passing it (even as
      false) also surfaces that layer's Layers-menu row outside research mode,
      so the client map can offer the tracts without the research overlays. */
  lrmTractsDefaultOn?: boolean;
  /** Hide the research base layers (pad rectangles, Sites A&C streams) and
      default them off — the client presentation has no use for them. */
  hideResearchBaseLayers?: boolean;
  /** Drop the ≥400 MW floor from the gas pipeline fetch so the small screened
      laterals (e.g. NET SOUTH 10.75") draw beside the trunk lines. Research
      keeps the portfolio-scale filter. */
  pipelineUnfiltered?: boolean;
  /** Surface the measure tool as a button under the map's zoom controls.
      Research keeps its toolbar pill + upper-left quick cluster instead. */
  measureControl?: boolean;
  /** Client-map overlay rows to add to the Layers menu, keyed by
      CLIENT_OVERLAY_ROWS. Absent leaves the research overlay rows untouched. */
  clientOverlays?: string[];
  /** Client presentation — the caller colored its rows by DECISION TIER
      (Advance → pass tone, everything else → caution) rather than by real
      knockout counts, so the marker legend must say tiers. The research
      legend ("Clean / 1 gate / ≥2 gates") would be a false claim there:
      every Advance site still carries an explicit primary gate. */
  tierMarkerLegend?: boolean;
}

/** Split connector geometry for one site's selected corridor. `label` strings
    are supplied by the caller so all client-facing wording lives in the page.
    `outsideDetail` is a preformatted multi-line block (owner attribution) the
    outside run's hover appends under its label. */
export interface ConnectorSegments {
  cp: GeoJSON.Geometry | null;
  outside: GeoJSON.Geometry | null;
  cpLabel?: string;
  outsideLabel?: string;
  outsideDetail?: string | null;
}

// Single source of truth for marker color — driven by knockout count rather
// than letter grade because for the current Integra cycle the grade is bunched
// at A+ (all curated as priority_1) and would render as a uniform green dot
// field. Knockouts give real visual variance: clean / one-gate / multi-gate.
function markerColor(count: number): { fill: string; border: string } {
  if (count === 0) return { fill: toneStyle('pass').fg, border: toneStyle('pass').border };
  if (count === 1) return { fill: toneStyle('caution').fg, border: toneStyle('caution').border };
  return { fill: toneStyle('fail').fg, border: toneStyle('fail').border };
}

const EMPTY_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

// FEMA 100-yr floodplain wash — matches designTokens.floodZone100yr and the
// main map's flood overlay so the color reads consistently across the product.
const FLOOD_COLOR = '#dc2626';
const TWDB_FLOOD_COLOR = '#2563eb';
const CLEANPAD_COLOR = '#15803d';  // clean buildable pad — flood + wetland netted out

// Zoom at/above which the TWDB overlay follows the map viewport instead of the
// whole cycle bbox, and the highest zoom the endpoint's precomputed display
// bands answer — above 14 it switches to the full-res path and rejects any bbox
// wider than a fraction of a degree, so never ask for more than 14.
const TWDB_VIEWPORT_MIN_ZOOM = 11;
const TWDB_MAX_DISPLAY_ZOOM = 14;

// The flood overlay is scoped to the Revenant East-TX AOI counties — flood in
// surrounding counties (Shelby, Houston, …) and in the Pecos/WV outlier
// site counties is suppressed so the overlay reads to the area of interest.
const FLOOD_AOI_STATE = 'TX';
const FLOOD_AOI_COUNTIES = ['Angelina', 'Jasper', 'Nacogdoches', 'San Augustine', 'Tyler', 'Trinity', 'Sabine'];
// Bounding box of the AOI counties' union. The flood overlay is a fixed region,
// so it's fetched ONCE for this bbox (not the moving viewport) — no per-pan
// refetch. Includes the three published county products.
const FLOOD_AOI_BBOX = '-95.85,30.20,-93.50,31.90';

// County boundary lines — a cool slate, dashed, so they read as administrative
// orientation rather than data (distinct from the solid pipeline lines).
const COUNTY_COLOR = '#64748b';

// County CAD parcel fabric — EVERY owner of record, not just Crown Pine. A
// background layer: barely-there fill (so the whole parcel interior answers a
// hover) plus a hairline edge. Cool slate keeps it out of the tan Crown Pine
// family. Dense enough that the endpoint only serves it from z13 up.
const CAD_FABRIC_COLOR = '#475569';
const CAD_FABRIC_LAYER_IDS = ['portfolio-cad-fabric-fill', 'portfolio-cad-fabric-line'];
const CAD_MIN_ZOOM = 13;

// All-Crown-Pine owner-parcel layer (research curation). Flat wash in a neutral
// timber tan so it reads as context beneath the curated tract shapes.
const OWNER_PARCEL_COLOR = '#a16207';
// Slope choropleth ramp for the owner-parcel layer — flat (green) → steep (red),
// driven by avg_slope_pct. East-TX CP land is mostly <10%, so the ramp is tuned
// tight at the low end to still show variance.
const SLOPE_FILL: mapboxgl.Expression = [
  'interpolate', ['linear'], ['coalesce', ['get', 'avg_slope_pct'], 0],
  0, '#15803d', 5, '#65a30d', 10, '#ca8a04', 15, '#ea580c', 25, '#b91c1c',
];

// Crown Pine research overlay layers (fetched lazily, viewport-based). Config-
// driven so the four share one install loop, one load effect, and one pill row.
// 'fill' = polygon disqualifier (habitat); 'line'/'circle' = infrastructure.
const CRITHAB_COLOR = '#c2410c';      // USFWS critical habitat — hazard orange (distinct from flood red)
const TRANSMISSION_COLOR = '#2563eb'; // HV transmission — power blue
const SUBSTATION_COLOR = '#1e3a8a';   // substations — deeper power blue
const HIGHWAY_COLOR = '#78716c';      // highways — muted stone (orientation)
const WELL_COLOR = '#1c1917';         // active wells — classic black well-spot, white stroke carries it on satellite
const DISTRIBUTION_COLOR = '#a8a29e'; // muted stone — deliberately quiet
const AIRPORT_COLOR = '#db2777';      // airports — magenta (aviation-chart convention), distinct from the power blues
// Pine Curtain play boundary — dissolved outline of the 8 play counties,
// served by /api/play-boundary. Deep pine green, inside the existing palette
// family; dashed like the county lines so it reads as orientation, not data.
const PLAY_BOUNDARY_COLOR = '#2E5E4E';
// Crown Pine LRM tracts — the landowner's OWN inventory (the KMZ they supplied
// 2026-08-21; /api/crownpine-parcels/tracts). Rust/amber by fund so it reads
// apart from the tan CAD-derived "All Crown Pine" wash and the teal other-owners.
const CP_TRACT_CPT1_COLOR = '#9a3412';
const CP_TRACT_CPT5_COLOR = '#d97706';
const CP_TRACT_FILL: mapboxgl.Expression = ['match', ['get', 'fund'], 'CPT5-Crown Pine Timber 5 LP', CP_TRACT_CPT5_COLOR, CP_TRACT_CPT1_COLOR];
const CP_TRACT_LAYER_IDS = ['cp-tract-fill', 'cp-tract-line', 'cp-tract-label'];
// "Other owners" research layer — the 13 standout non-Crown-Pine assemblies from
// the 2026-07-20 East Texas 500-ac screen. One teal family (unused elsewhere on
// this map so it can't be read as Crown Pine land), role-differentiated: tract
// wash, thin member-parcel lines, bright buildable-pad fill, and a bold dashed
// outline for the screen's winning 500-ac rectangle.
const OTHER_TRACT_COLOR = '#0d9488';   // teal-600 — tract boundary + wash
const OTHER_PARCEL_COLOR = '#0f766e';  // teal-700 — member-parcel lines
const OTHER_PAD_COLOR = '#2dd4bf';     // teal-400 — buildable pads
const OTHER_RECT_COLOR = '#134e4a';    // teal-900 — winning 500-ac rectangle
const OTHER_OWNER_LAYER_IDS = ['oo-tract-fill', 'oo-tract-line', 'oo-parcel-line', 'oo-pad-fill', 'oo-pad-line', 'oo-pad-label', 'oo-rect-line'];
// Crown Pine pad rectangles — the rotation-optimized placements from the
// stream/WOTUS screens (site memos + 30-block screen). Darker than the pad
// fill so the rectangle reads as a drawn plan on top of the buildable area.
const CP_RECT_COLOR = '#14532d';
const CP_RECT_LAYER_IDS = ['cp-rect-line', 'cp-rect-label'];
// Site A & C streams (NHD) — water blues, distinct from the power blues by
// leaning cyan + carrying dash patterns. Perennial solid, intermittent dashed;
// the AJD-case drains render as ordinary intermittent streams (no red highlight).
const STREAM_PER_COLOR = '#0369a1';   // perennial — solid deep water blue
const STREAM_INT_COLOR = '#0ea5e9';   // intermittent — dashed lighter blue
const STREAM_LAYER_IDS = ['ss-per-line', 'ss-int-line', 'ss-name-label'];
// Client-map NHDPlus HR flowlines across the LRM blocks — a separate, optional
// context layer (clientOverlays 'streams'), NOT the Sites A&C research layer
// above. Reuses the same two water blues so streams read the same everywhere.
const LRM_STREAM_LAYER_IDS = ['lrm-stream-perennial', 'lrm-stream-intermittent'];
// Build-overable streambeds — the mapped NWI R4 riverine polygons the effective
// buildable basis treats as engineered crossings rather than severing features.
// Same intermittent-stream blue as the Streams row (it IS the same water), but
// a 1px dotted hairline so a streambed and an NHD flowline stay apart when both
// are on. The endpoint takes no zoom and 400s above 0.25° per axis, so the span
// gate lives on the client (see loadStreambeds).
const STREAMBED_LAYER_ID = 'portfolio-streambeds-line';
const STREAMBED_MAX_SPAN_DEG = 0.25;
// A1 acquisition premises — the outline of the real premises inside its much
// larger opportunity block. Deliberately drawn in ink (heading / secondary)
// rather than a data hue: it is a land-identity boundary, not another measured
// layer, and an outline with no fill keeps the buildable highlight beneath it
// legible. Swapped to the page haze on satellite, the same move the county
// lines make. Division line first so the 2px boundary draws over it.
const A1_PREMISES_LAYER_IDS = ['a1-premises-division', 'a1-premises-line'];
const A1_PREMISES_LEGEND = 'A1 premises · 3,037.93 ac';
// Operator-family styling for the client presentation (pipelineFamilyStyling).
// Energy Transfer reuses the main map's gas-transmission color so the family
// reads the same across the product (designTokens.colors.pipelineGasTransmission,
// MapView.tsx PIPELINE_GROUPS); Kinder Morgan is a desaturated sage that sits
// behind it without competing.
const ET_PIPELINE_COLOR = designTokens.colors.pipelineGasTransmission;
const KM_PIPELINE_COLOR = '#7A8F7E';
// The two screened families. Applied as a LAYER FILTER (not a transparent
// paint) to both the drawn pipeline and its hit strip, so a hidden operator is
// neither visible nor hoverable.
const PIPELINE_FAMILY_FILTER: mapboxgl.Expression =
  ['match', ['get', 'operator_parent'], ['Energy Transfer', 'Kinder Morgan'], true, false];
// Transparent wide twin of the pipeline layer — the hover target (see install).
const PIPELINE_HIT_LAYER = 'portfolio-pipelines-hit';
// Screened campus-to-tap connector, split by land control. Palette-native:
// REPORT.pass for the on-inventory run, REPORT.fail for the outside run.
const CONNECTOR_CP_COLOR = REPORT.pass;
const CONNECTOR_OUTSIDE_COLOR = REPORT.fail;
const CONNECTOR_CAVEAT = 'Straight-line screen using seller-supplied tract geometry and public pipeline centerlines. Not a surveyed route, title opinion, easement determination, or operator-approved tap.';
type OverlayKind = 'fill' | 'line' | 'circle';
interface ResearchOverlay { key: string; label: string; color: string; kind: OverlayKind; url: (bbox: string) => string; dash?: [number, number]; }
const FIBER_COLOR = '#ca8a04';        // FiberLight fiber — gold, the one hue not held by power/water/soil/pads
const RESEARCH_OVERLAYS: ResearchOverlay[] = [
  { key: 'crithab',      label: 'Habitat',      color: CRITHAB_COLOR,      kind: 'fill',   url: b => `/api/critical-habitat/geojson?bbox=${b}` },
  { key: 'transmission', label: 'Transmission', color: TRANSMISSION_COLOR, kind: 'line',   url: b => `/api/power/geojson?type=transmission_line&min_voltage=100&bbox=${b}&include=overlay` },
  { key: 'substations',  label: 'Substations',  color: SUBSTATION_COLOR,   kind: 'circle', url: b => `/api/power/geojson?type=substation&min_voltage=69&bbox=${b}&include=overlay&class=transmission` },
  { key: 'dist',         label: 'Dist. yards',  color: DISTRIBUTION_COLOR, kind: 'circle', url: b => `/api/power/geojson?type=substation&bbox=${b}&include=overlay&class=distribution` },
  { key: 'highways',     label: 'Highways',     color: HIGHWAY_COLOR,      kind: 'line',   url: b => `/api/highways/geojson?bbox=${b}` },
  // FiberLight fiber near the sites — the Zavalla/Site A corridor (within ~0.5 mi
  // of Zavalla). Committed seed, served whole; rebuild build-crownpine-fiber.js.
  { key: 'fiber',        label: 'Fiber (FiberLight)', color: FIBER_COLOR,  kind: 'line',   url: b => `/api/crownpine-parcels/site-fiber?bbox=${b}` },
  // Active (non-plugged) wells INSIDE Crown Pine parcels only — ~450 points,
  // served whole (the endpoint ignores bbox) so the layer stays tiny.
  { key: 'wells',        label: 'Wells',        color: WELL_COLOR,         kind: 'circle', url: b => `/api/crownpine-parcels/wells?bbox=${b}` },
  // Airports near the Revenant AOI (within 20 mi) ∪ the 3 nearest large-commercial
  // airports (runway ≥ 8000 ft) even if farther — a ~9-point served-whole set.
  { key: 'airports',     label: 'Airports',     color: AIRPORT_COLOR,      kind: 'circle', url: b => `/api/crownpine-parcels/airports?bbox=${b}` },
];
// Railroads (HIFLD, /api/railroads/geojson). Client-map layer only — it is NOT
// in RESEARCH_OVERLAYS, so the research page gains no row and no fetch. Thin
// dark dashes so heavy-haul rail reads apart from the solid pipeline lines.
const RAILROAD_COLOR = '#3f3f46';
const RAILROAD_OVERLAY: ResearchOverlay = {
  key: 'railroads', label: 'Railroads', color: RAILROAD_COLOR, kind: 'line',
  dash: [4, 2], url: b => `/api/railroads/geojson?bbox=${b}`,
};
// County-context wells. Client-map layer only, like railroads — the research
// page keeps its tighter on-CP-land Wells row above. Every active well in the
// counties holding Crown Pine inventory (~22K points, half in Panola + Rusk);
// served whole with a day of browser cache, so the moveend refetch is a cache
// hit, not a re-download.
const COUNTY_WELLS_OVERLAY: ResearchOverlay = {
  key: 'countyWells', label: 'Wells', color: WELL_COLOR, kind: 'circle',
  url: b => `/api/crownpine-parcels/county-wells?bbox=${b}`,
};
// County-context carrier fiber (client map only) — every known route in the
// 19 CP-inventory counties (FiberLight + Zayo + Segra, ~1,020 mi), replacing
// the frozen Zavalla-corridor clip the research page's FiberLight row keeps.
const COUNTY_FIBER_OVERLAY: ResearchOverlay = {
  key: 'countyFiber', label: 'Fiber', color: FIBER_COLOR, kind: 'line',
  url: b => `/api/crownpine-parcels/county-fiber?bbox=${b}`,
};
// Every overlay the map installs + loads. Research rows still come from
// RESEARCH_OVERLAYS; client rows come from CLIENT_OVERLAY_ROWS.
const MAP_OVERLAYS: ResearchOverlay[] = [...RESEARCH_OVERLAYS, RAILROAD_OVERLAY, COUNTY_WELLS_OVERLAY, COUNTY_FIBER_OVERLAY];
// Layers-menu rows for the client presentation (the `clientOverlays` prop names
// which ones to surface). Each row reuses the overlay configs above; `keys`
// lets one row drive two overlays, so Power toggles lines + substations at once.
const CLIENT_OVERLAY_ROWS: Record<string, { label: string; color: string; keys: string[] }> = {
  airports:  { label: 'Airports',  color: AIRPORT_COLOR,      keys: ['airports'] },
  railroads: { label: 'Railroads', color: RAILROAD_COLOR,     keys: ['railroads'] },
  highways:  { label: 'Highways',  color: HIGHWAY_COLOR,      keys: ['highways'] },
  fiber:     { label: 'Fiber',     color: FIBER_COLOR,        keys: ['countyFiber'] },
  power:     { label: 'Power · lines & substations', color: TRANSMISSION_COLOR, keys: ['transmission', 'substations'] },
  wells:     { label: 'Wells', color: WELL_COLOR,    keys: ['countyWells'] },
};
const NO_CLIENT_OVERLAYS: string[] = [];
const overlayLayerIds = (o: ResearchOverlay) =>
  o.kind === 'fill' ? [`ovl-${o.key}-fill`, `ovl-${o.key}-line`]
  : o.kind === 'line' ? [`ovl-${o.key}`, `ovl-${o.key}-planned`, `ovl-${o.key}-points`]
  : [`ovl-${o.key}`];

// One row of the Layers menu. `sub` is an optional nested toggle (used by "All
// Crown Pine" for its shade-by-slope option), shown indented when the parent is on.
interface LayerSub { label: string; active: boolean; onToggle: () => void; }
interface LayerItem { key: string; label: string; color: string; active: boolean; onToggle: () => void; sub?: LayerSub; }
// A non-interactive section label between rows (client menu grouping).
interface LayerHeading { heading: string; }
type LayerMenuEntry = LayerItem | LayerHeading;
const isHeading = (e: LayerMenuEntry): e is LayerHeading => 'heading' in e;

export const CrownPineMap: React.FC<Props> = ({
  rows,
  cycleBbox,
  onSelect,
  onHover,
  height = 380,
  stuckHeight = 360,
  sticky = true,
  activeParcelId = null,
  shareToken,
  ownerParcels = false,
  pads,
  activePadId = null,
  focusPadId = null,
  hoveredPadId = null,
  onSelectPad,
  onHoverPad,
  pipelineFamilyStyling = false,
  connector = null,
  buildableHighlight = false,
  lrmTractsDefaultOn,
  hideResearchBaseLayers = false,
  pipelineUnfiltered = false,
  measureControl = false,
  clientOverlays = NO_CLIENT_OVERLAYS,
  tierMarkerLegend = false,
}) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  // Separate popup for the All-CP owner-parcel hover (kept distinct from the
  // tract-dot popup so hovering a parcel under a dot doesn't fight it).
  const ownerPopupRef = useRef<mapboxgl.Popup | null>(null);
  // Per-pad hover popup (Crown Pine pad mode). Distinct ref so it can't fight
  // the tract-dot or owner-parcel popups.
  const padPopupRef = useRef<mapboxgl.Popup | null>(null);
  // Hover popup shared by the line layers (transmission overlay + gas pipelines).
  const linePopupRef = useRef<mapboxgl.Popup | null>(null);
  // County CAD fabric hover — its own ref so the owner-of-record popup can't
  // fight the curated tract / pad popups above it.
  const cadPopupRef = useRef<mapboxgl.Popup | null>(null);
  // Latest pad-mode inputs, read inside the once-installed map handlers so they
  // stay current without re-binding. padModeRef gates the pad handlers OFF for
  // Integra (which never passes `pads`) → its clean-pad layer is inert as before.
  const padModeRef = useRef(false);
  const padsRef = useRef<PortfolioPad[] | undefined>(pads);
  const onSelectPadRef = useRef(onSelectPad);
  const onHoverPadRef = useRef(onHoverPad);
  useEffect(() => { padModeRef.current = !!(pads && pads.length); padsRef.current = pads; }, [pads]);
  useEffect(() => { onSelectPadRef.current = onSelectPad; }, [onSelectPad]);
  useEffect(() => { onHoverPadRef.current = onHoverPad; }, [onHoverPad]);
  const [ready, setReady] = useState(false);
  const [stuck, setStuck] = useState(false);
  const [hoverParcel, setHoverParcel] = useState<string | null>(null);
  // Buildable piece under the cursor (buildableHighlight mode). Map hover is
  // per-PIECE — a site's other pieces stay quiet — while list-row hover keeps
  // ringing the whole site. Set here rather than pushed to the parent because
  // no caller has any use for a piece id.
  const [hoveredPieceId, setHoveredPieceId] = useState<string | null>(null);
  const [styleMode, setStyleMode] = useState<MapStyleMode>('light');
  // Live cursor position for the corner readout. Fed by a throttled (~10/sec)
  // map-wide mousemove in the mount effect; null whenever the cursor is off
  // the canvas, which is what hides the chip.
  const [cursorCoords, setCursorCoords] = useState<{ lat: number; lng: number } | null>(null);
  // "Assemblies" (false, default) shows only the dissolved outlines; "Parcels"
  // (true) additionally reveals the member-parcel subdivisions within each block.
  const [showParcels, setShowParcels] = useState(false);
  // FEMA 100-yr floodplain overlay — off by default so the map opens clean;
  // the viewer flips it on to see the flood context behind each tract.
  const [showFlood, setShowFlood] = useState(false);
  const [pendingFloodLabels, setPendingFloodLabels] = useState<string[]>([]);
  const [floodTruncated, setFloodTruncated] = useState({ effective: false, pending: false });
  const floodRequestRef = useRef(0);
  // TWDB State Flood Plan existing 1% annual-chance hazard. This planning
  // layer stays independent from FEMA's regulatory floodplain overlay.
  const [showTwdbFlood, setShowTwdbFlood] = useState(false);
  // Which band the TWDB overlay is currently holding — 'aoi' = the whole-cycle
  // band-8 silhouette, 'viewport' = a detailed band cut to the current bounds.
  // Null until a response is applied. Read by the moveend handler to skip the
  // refetch when a low-zoom pan would only re-ask for the same silhouette.
  const twdbFloodModeRef = useRef<'aoi' | 'viewport' | null>(null);
  // Monotonic request id, same guard as the viewport loaders below.
  const twdbFloodSeqRef = useRef(0);
  // Clean buildable pad overlay — ON by default; it's the point of this view.
  const [showCleanPad, setShowCleanPad] = useState(true);
  // The screened campus placement, in buildableHighlight mode only. The
  // buildable acreage is the headline; the placement is the drawn plan on top
  // of it, so it starts off and the viewer asks for it.
  const [showCampusPlacement, setShowCampusPlacement] = useState(false);
  // Build-overable streambeds (NWI R4 riverine) — off by default on both pages.
  // In buildableHighlight mode this single row is the STREAM-CUTS switch: it
  // re-bases the highlight to the conservative screened pieces AND draws the
  // beds that severed them, so the reduction and its cause arrive together
  // (Matthew 2026-08-31: one click, not two). On the research page — whole
  // pads, no basis to swap — it stays a lines-only context layer.
  // streambedHint mirrors cadHint: the note shown when the viewport is wider
  // than the endpoint accepts and nothing can be requested.
  const [showStreambeds, setShowStreambeds] = useState(false);
  const [streambedHint, setStreambedHint] = useState<string | null>(null);
  const streambedSeqRef = useRef(0);
  // Which basis the buildable highlight draws (buildableHighlight mode only):
  // the effective-contiguous pieces by default — rejoined across mapped
  // intermittent streambeds with the streambed acreage deducted — or the
  // conservative screened pieces while the stream cuts are shown. DERIVED from
  // that one switch rather than a control of its own: exactly one of the two
  // bases is ever on the map, and padLayerLabel names which.
  const showEffectiveBuildable = !showStreambeds;
  // County boundary lines — orientation reference, off by default.
  const [showCounties, setShowCounties] = useState(false);
  // County CAD parcel fabric (every owner of record) — off by default on both
  // pages. cadHint carries the legend note when the layer can't show anything
  // useful (zoomed out) or is showing a capped slice.
  const [showCadFabric, setShowCadFabric] = useState(false);
  const [cadHint, setCadHint] = useState<string | null>(null);
  // Monotonic request id — a response from a viewport the user has already
  // panned away from is dropped rather than painted.
  const cadSeqRef = useRef(0);
  // Crown Pine research layers (only surfaced when ownerParcels prop is set).
  // "All CP" paints every Crown Pine-owned parcel for visual curation; "Slope"
  // switches that layer's fill from a flat wash to a flat→steep choropleth.
  const [showOwnerParcels, setShowOwnerParcels] = useState(false);
  const [showSlope, setShowSlope] = useState(false);
  // Same monotonic-request-id guard as cadSeqRef, for the other three
  // viewport-following loaders. Without it two in-flight viewport responses
  // can land out of order and the stale one paints last.
  const countiesSeqRef = useRef(0);
  const ownerParcelsSeqRef = useRef(0);
  const cpTractsSeqRef = useRef(0);
  // Pine Curtain play boundary (dissolved 8-county outline) — ON by default in
  // research mode: it's the play's identity. Its Layers-menu row only exists in
  // research mode, so outside it the layer starts (and stays) off rather than
  // rendering with no way to turn it back off.
  const [showPlayBoundary, setShowPlayBoundary] = useState(ownerParcels);
  // Crown Pine's own LRM tract inventory — ON by default in research mode: it
  // is the seller's statement of what they own, the ground the pads sit on.
  // lrmTractsDefaultOn overrides that (the client map keeps them off).
  const [showCpTracts, setShowCpTracts] = useState(lrmTractsDefaultOn ?? ownerParcels);
  // The tracts row is a research layer by default, but any caller that sets
  // lrmTractsDefaultOn is asking for it explicitly.
  const cpTractsAvailable = ownerParcels || lrmTractsDefaultOn !== undefined;
  // Other-owners overlay (13 standout non-CP assemblies from the 500-ac
  // screen). Off by default; fixed geometry fetched once when first shown.
  const [showOtherOwners, setShowOtherOwners] = useState(false);
  // Crown Pine pad rectangles (stream/WOTUS-screen placements) — ON by
  // default: they're the buildable-plan answer the pads gesture at.
  const [showRects, setShowRects] = useState(!hideResearchBaseLayers);
  // Site A & C streams — ON by default: shows WHY the rectangles sit where
  // they sit (site-local data, invisible until you're near Sites A/C).
  const [showStreams, setShowStreams] = useState(!hideResearchBaseLayers);
  // Client-map LRM streams — offered only when the caller lists 'streams' in
  // clientOverlays, and OFF until asked for. The research page never passes it,
  // so nothing below (source, layers, row, legend, hover) exists there.
  const lrmStreamsAvailable = clientOverlays.includes('streams');
  const [showLrmStreams, setShowLrmStreams] = useState(false);
  // Served-whole FeatureCollection: fetched once on first toggle-on, replayed
  // from here after a style swap wipes the source.
  const lrmStreamsCacheRef = useRef<GeoJSON.FeatureCollection | null>(null);
  // A1 acquisition premises outline — ON by default on the client presentation
  // (buildableHighlight): the flagship's identity, and the premises is a small
  // fraction of the opportunity block it sits in, so leaving it off would let
  // the block read as the thing being acquired. Offered but OFF on the research
  // page. Static geometry, so it uses the same fetch-once + ref-cache pattern
  // as the LRM streams above.
  const [showA1Premises, setShowA1Premises] = useState(buildableHighlight);
  const a1PremisesCacheRef = useRef<GeoJSON.FeatureCollection | null>(null);
  // Which research overlays (critical habitat / transmission / substations /
  // highways) are on. A set keyed by overlay.key; toggled by the pill row.
  const [overlays, setOverlays] = useState<Set<string>>(new Set());
  const toggleOverlay = useCallback((key: string) => {
    setOverlays(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);
  // Flip a whole client row's overlay keys together (Power drives two).
  const setOverlayKeys = useCallback((keys: string[], on: boolean) => {
    setOverlays(prev => {
      const next = new Set(prev);
      for (const k of keys) on ? next.add(k) : next.delete(k);
      return next;
    });
  }, []);
  // Gas pipelines — always-on layer, but the client map offers a row to hide it
  // so the terrain and the other corridors can be read underneath.
  const [showPipelines, setShowPipelines] = useState(true);
  // Bumped every time installLayers finishes (initial mount + each style
  // swap). All data-dependent effects include this in their deps so they
  // re-fire and repopulate sources / re-apply paint after a swap. This is
  // more reliable than toggling `ready` because React batching can collapse
  // a false→true flip and skip the intermediate state.
  const [styleEpoch, setStyleEpoch] = useState(0);

  // Measure tools (research mode, or any caller passing measureControl): the
  // click-to-measure distance run and the drag-a-rectangle acreage read. Only
  // one can be armed at a time. measureActiveRef folds BOTH into the single
  // guard every hover/click handler already checks, so no handler needs to
  // learn about the second mode.
  const [measureActive, setMeasureActive] = useState(false);
  const [areaActive, setAreaActive] = useState(false);
  const measureActiveRef = useRef(false);
  measureActiveRef.current = measureActive || areaActive;
  const armMeasure = useCallback((v: boolean) => { setMeasureActive(v); if (v) setAreaActive(false); }, []);
  const armArea = useCallback((v: boolean) => { setAreaActive(v); if (v) setMeasureActive(false); }, []);
  const disarmArea = useCallback(() => setAreaActive(false), []);
  const measure = useMapMeasurement({
    mapRef, ready, styleEpoch,
    active: measureActive,
    areaActive,
    onExitArea: disarmArea,
    enabled: ownerParcels || measureControl,
  });

  // Refs hold the latest callbacks so the once-mounted map handlers always
  // call the current parent state setters, not the initial closure.
  const onSelectRef = useRef(onSelect);
  const onHoverRef = useRef(onHover);
  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);
  useEffect(() => { onHoverRef.current = onHover; }, [onHover]);

  // Detect when the user has scrolled past the map's "natural" position.
  // We observe a 1px sentinel placed just above the map shell; when it leaves
  // the top of the viewport, the map is stuck. Sticky positioning handles the
  // visual lock — this just toggles the compact-mode height + drop shadow.
  useEffect(() => {
    if (!sticky || !sentinelRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => setStuck(!entry.isIntersecting && entry.boundingClientRect.top < 0),
      { threshold: 0, rootMargin: '0px 0px 0px 0px' }
    );
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [sticky]);

  // When the container height swaps (full ↔ stuck), Mapbox needs to recompute
  // canvas dimensions. resize() is cheap and idempotent.
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    // RAF avoids racing the CSS height transition end.
    requestAnimationFrame(() => mapRef.current?.resize());
  }, [stuck, ready]);

  // Power popups are installed by installLayers below; this decides for whom.
  const powerPopups = ownerParcels || clientOverlays.includes('power');

  // Install all portfolio-specific sources, layers, and event handlers on the
  // map. Called once after initial style load AND again every time the user
  // toggles base style (setStyle wipes all user-added sources/layers).
  // Pipeline line color is slightly brighter on satellite so it still reads
  // against the darker imagery.
  const installLayers = useCallback((m: mapboxgl.Map, mode: MapStyleMode) => {
    const isSat = mode === 'satellite';

    // FEMA 100-yr floodplain overlay — added before everything else so the
    // wash sits at the very bottom (site shapes, pipelines, dots all draw over
    // it). Data + visibility are driven by the showFlood toggle effect below;
    // the source starts empty and the layers hidden.
    m.addSource('portfolio-flood', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'portfolio-flood-fill',
      source: 'portfolio-flood',
      type: 'fill',
      layout: { visibility: 'none' },
      paint: { 'fill-color': ['case', ['==', ['get', 'flood_type'], '500yr'], '#f97316', FLOOD_COLOR], 'fill-opacity': isSat ? 0.34 : 0.22 },
    });
    m.addLayer({
      id: 'portfolio-flood-line',
      source: 'portfolio-flood',
      type: 'line',
      layout: { visibility: 'none' },
      paint: { 'line-color': ['case', ['==', ['get', 'flood_type'], '500yr'], '#f97316', FLOOD_COLOR], 'line-width': 0.5, 'line-opacity': 0.5 },
    });

    m.addSource('portfolio-pending-flood', { type: 'geojson', data: EMPTY_FC });
    for (const layer of pendingFloodLayers('portfolio-pending-flood', 'portfolio-pending-flood-fill', 'portfolio-pending-flood-line')) {
      m.addLayer({ ...layer, layout: { visibility: 'none' } });
    }

    // TWDB existing 1% annual-chance hazard — a separate blue planning wash,
    // never merged with or represented as the FEMA regulatory floodplain.
    m.addSource('portfolio-twdb-flood', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'portfolio-twdb-flood-fill',
      source: 'portfolio-twdb-flood',
      type: 'fill',
      layout: { visibility: 'none' },
      paint: { 'fill-color': TWDB_FLOOD_COLOR, 'fill-opacity': isSat ? 0.34 : 0.22 },
    });
    m.addLayer({
      id: 'portfolio-twdb-flood-line',
      source: 'portfolio-twdb-flood',
      type: 'line',
      layout: { visibility: 'none' },
      paint: { 'line-color': TWDB_FLOOD_COLOR, 'line-width': 0.7, 'line-opacity': 0.65 },
    });

    // County boundary lines — dashed slate, above the flood wash but beneath the
    // site shapes so tracts stay prominent. Data + visibility driven by the
    // showCounties toggle effect below.
    m.addSource('portfolio-counties', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'portfolio-counties-line',
      source: 'portfolio-counties',
      type: 'line',
      layout: { visibility: 'none' },
      paint: {
        'line-color': isSat ? '#CBD5E1' : COUNTY_COLOR,
        'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0.5, 10, 1, 14, 1.4],
        'line-opacity': isSat ? 0.7 : 0.55,
        'line-dasharray': [3, 2],
      },
    });
    // County name labels — same source and toggle as the boundary lines.
    m.addLayer({
      id: 'portfolio-counties-label',
      source: 'portfolio-counties',
      type: 'symbol',
      layout: {
        visibility: 'none',
        'text-field': ['get', 'county_name'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 6, 9, 10, 12],
        'text-transform': 'uppercase',
        'text-letter-spacing': 0.12,
      },
      paint: {
        'text-color': isSat ? '#E2E8F0' : COUNTY_COLOR,
        'text-halo-color': isSat ? 'rgba(15,23,42,0.75)' : '#FFFFFF',
        'text-halo-width': 1.2,
        'text-opacity': 0.8,
      },
    });

    // Pine Curtain play boundary — dissolved outline of the 8 play counties.
    // Fixed geometry: empty + hidden here, populated + shown by loadPlayBoundary.
    m.addSource('portfolio-play-boundary', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'portfolio-play-boundary-fill',
      source: 'portfolio-play-boundary',
      type: 'fill',
      layout: { visibility: 'none' },
      paint: { 'fill-color': PLAY_BOUNDARY_COLOR, 'fill-opacity': 0.04 },
    });
    m.addLayer({
      id: 'portfolio-play-boundary-line',
      source: 'portfolio-play-boundary',
      type: 'line',
      layout: { visibility: 'none' },
      paint: { 'line-color': PLAY_BOUNDARY_COLOR, 'line-width': 2, 'line-dasharray': [3, 2] },
    });

    // County CAD parcel fabric — added BEFORE the tracts / owner parcels / site
    // shapes so it reads as background under everything curated. The fill is
    // near-invisible but present: hit-testing needs a fill to answer a hover
    // anywhere inside a parcel, not just on its edge. Zoom-gated to match the
    // endpoint, which refuses to serve the county fabric below z13.
    m.addSource('portfolio-cad-fabric', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'portfolio-cad-fabric-fill',
      source: 'portfolio-cad-fabric',
      type: 'fill',
      minzoom: CAD_MIN_ZOOM,
      layout: { visibility: 'none' },
      paint: { 'fill-color': CAD_FABRIC_COLOR, 'fill-opacity': 0.04 },
    });
    m.addLayer({
      id: 'portfolio-cad-fabric-line',
      source: 'portfolio-cad-fabric',
      type: 'line',
      minzoom: CAD_MIN_ZOOM,
      layout: { visibility: 'none' },
      paint: { 'line-color': CAD_FABRIC_COLOR, 'line-width': 0.5, 'line-opacity': isSat ? 0.7 : 0.45 },
    });

    // Crown Pine LRM tracts (landowner-supplied inventory). Viewport-fetched by
    // loadCpTracts; sits beneath the CAD owner-parcel wash and everything curated.
    m.addSource('portfolio-cp-tracts', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'cp-tract-fill', source: 'portfolio-cp-tracts', type: 'fill',
      layout: { visibility: 'none' },
      paint: { 'fill-color': CP_TRACT_FILL, 'fill-opacity': isSat ? 0.28 : 0.18 },
    });
    m.addLayer({
      id: 'cp-tract-line', source: 'portfolio-cp-tracts', type: 'line',
      layout: { visibility: 'none' },
      paint: { 'line-color': CP_TRACT_FILL, 'line-width': 0.8, 'line-opacity': isSat ? 0.9 : 0.7 },
    });
    m.addLayer({
      id: 'cp-tract-label', source: 'portfolio-cp-tracts', type: 'symbol',
      minzoom: 11,
      layout: {
        visibility: 'none',
        'text-field': ['get', 'tract_name'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 11, 9, 13, 11],
        'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
      },
      paint: { 'text-color': '#7c2d12', 'text-halo-color': 'rgba(255,255,255,0.9)', 'text-halo-width': 1.2 },
    });

    // All-Crown-Pine owner-parcel layer (research curation, Crown Pine mode only).
    // Empty + hidden by default; populated + shown by the showOwnerParcels effect.
    // Sits beneath the curated tract shapes so it reads as context. The fill color
    // flips between a flat tan and the slope choropleth via the showSlope toggle.
    m.addSource('portfolio-owner-parcels', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'portfolio-owner-parcels-fill',
      source: 'portfolio-owner-parcels',
      type: 'fill',
      layout: { visibility: 'none' },
      paint: { 'fill-color': OWNER_PARCEL_COLOR, 'fill-opacity': isSat ? 0.3 : 0.2 },
    });
    m.addLayer({
      id: 'portfolio-owner-parcels-line',
      source: 'portfolio-owner-parcels',
      type: 'line',
      layout: { visibility: 'none' },
      paint: { 'line-color': OWNER_PARCEL_COLOR, 'line-width': 0.5, 'line-opacity': isSat ? 0.8 : 0.55 },
    });

    // Other owners — the 13 standout non-CP assemblies from the 500-ac screen
    // (tract / parcel / pad / rect features on one source, split by
    // feature_kind). Context layer like the AOI: empty + hidden by default,
    // fetched once by the loadOtherOwners effect. Sits beneath the CP outlines
    // and pads so Crown Pine content stays on top.
    m.addSource('portfolio-other-owners', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'oo-tract-fill', source: 'portfolio-other-owners', type: 'fill',
      filter: ['==', ['get', 'feature_kind'], 'tract'],
      layout: { visibility: 'none' },
      paint: { 'fill-color': OTHER_TRACT_COLOR, 'fill-opacity': isSat ? 0.16 : 0.10 },
    });
    m.addLayer({
      id: 'oo-tract-line', source: 'portfolio-other-owners', type: 'line',
      filter: ['==', ['get', 'feature_kind'], 'tract'],
      layout: { visibility: 'none' },
      paint: { 'line-color': OTHER_TRACT_COLOR, 'line-width': 2, 'line-opacity': isSat ? 0.95 : 0.8 },
    });
    m.addLayer({
      id: 'oo-parcel-line', source: 'portfolio-other-owners', type: 'line',
      filter: ['==', ['get', 'feature_kind'], 'parcel'],
      layout: { visibility: 'none' },
      paint: { 'line-color': OTHER_PARCEL_COLOR, 'line-width': 0.7, 'line-opacity': isSat ? 0.8 : 0.55 },
    });
    m.addLayer({
      id: 'oo-pad-fill', source: 'portfolio-other-owners', type: 'fill',
      filter: ['==', ['get', 'feature_kind'], 'pad'],
      layout: { visibility: 'none' },
      paint: { 'fill-color': OTHER_PAD_COLOR, 'fill-opacity': isSat ? 0.4 : 0.28 },
    });
    m.addLayer({
      id: 'oo-pad-line', source: 'portfolio-other-owners', type: 'line',
      filter: ['==', ['get', 'feature_kind'], 'pad'],
      layout: { visibility: 'none' },
      paint: { 'line-color': OTHER_PAD_COLOR, 'line-width': 1, 'line-opacity': isSat ? 0.95 : 0.7 },
    });
    m.addLayer({
      id: 'oo-pad-label', source: 'portfolio-other-owners', type: 'symbol',
      filter: ['==', ['get', 'feature_kind'], 'pad'],
      minzoom: 9.5,
      layout: {
        visibility: 'none',
        'text-field': ['concat', ['to-string', ['get', 'acres']], ' ac'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 9.5, 9.5, 13, 12],
        'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
      },
      paint: {
        'text-color': '#0f766e',
        'text-halo-color': 'rgba(255,255,255,0.9)',
        'text-halo-width': 1.2,
      },
    });
    m.addLayer({
      id: 'oo-rect-line', source: 'portfolio-other-owners', type: 'line',
      filter: ['==', ['get', 'feature_kind'], 'rect'],
      layout: { visibility: 'none' },
      paint: { 'line-color': OTHER_RECT_COLOR, 'line-width': 2.5, 'line-dasharray': [3, 2], 'line-opacity': 0.95 },
    });

    // Research + client overlays (critical habitat / transmission / substations /
    // highways / railroads / …). Empty + hidden by default; populated + shown by
    // the loadOverlays effect once a Layers row turns one on.
    for (const o of MAP_OVERLAYS) {
      m.addSource(`ovl-${o.key}`, { type: 'geojson', data: EMPTY_FC });
      if (o.kind === 'fill') {
        m.addLayer({
          id: `ovl-${o.key}-fill`, source: `ovl-${o.key}`, type: 'fill',
          layout: { visibility: 'none' },
          paint: { 'fill-color': o.color, 'fill-opacity': isSat ? 0.4 : 0.28 },
        });
        m.addLayer({
          id: `ovl-${o.key}-line`, source: `ovl-${o.key}`, type: 'line',
          layout: { visibility: 'none' },
          paint: { 'line-color': o.color, 'line-width': 0.8, 'line-opacity': 0.7 },
        });
      } else if (o.kind === 'line') {
        m.addLayer({
          id: `ovl-${o.key}`, source: `ovl-${o.key}`, type: 'line',
          layout: { visibility: 'none', 'line-cap': 'round' },
          filter: ['==', ['coalesce', ['get', 'operational_status'], 'existing'], 'existing'],
          paint: {
            'line-color': o.color,
            'line-width': ['interpolate', ['linear'], ['zoom'], 6, 0.6, 11, 1.6, 14, 2.6],
            'line-opacity': isSat ? 0.9 : 0.7,
            ...(o.dash ? { 'line-dasharray': o.dash } : {}),
          },
        });
        // Dashed twin for planned/proposed/candidate segments — dasharray isn't
        // data-drivable in Mapbox GL, so non-existing status gets its own layer.
        m.addLayer({
          id: `ovl-${o.key}-planned`, source: `ovl-${o.key}`, type: 'line',
          layout: { visibility: 'none', 'line-cap': 'round' },
          filter: ['!=', ['coalesce', ['get', 'operational_status'], 'existing'], 'existing'],
          paint: { 'line-color': o.color, 'line-width': 1.4, 'line-dasharray': [2, 2], 'line-opacity': 0.8 },
        });
        // Point-geometry twin — endpoint-only circuits (geometry_confidence
        // 'endpoint_only', incl. one existing 345kV line) carry a Point, not
        // a LineString/MultiLineString, so the two line layers above never
        // render them. Small hollow-ring marker reusing the same
        // status-aware stroke semantics as the substation/dist circles
        // (hollow fill for non-existing, muted ring for unverified candidates).
        m.addLayer({
          id: `ovl-${o.key}-points`, source: `ovl-${o.key}`, type: 'circle',
          layout: { visibility: 'none' },
          filter: ['==', ['geometry-type'], 'Point'],
          paint: {
            'circle-radius': 3.5,
            'circle-color': [
              'case',
              ['!=', ['coalesce', ['get', 'operational_status'], 'existing'], 'existing'],
              'rgba(0,0,0,0)',
              o.color,
            ],
            'circle-stroke-width': 1.4,
            'circle-stroke-color': [
              'case',
              ['==', ['coalesce', ['get', 'operational_status'], 'existing'], 'candidate'],
              DISTRIBUTION_COLOR,
              o.color,
            ],
            'circle-opacity': 0.9,
          },
        });
      } else {
        // Status-aware paint (hollow for planned/candidate, muted ring for
        // unverified) only applies to the power overlays (substations/dist) —
        // wells and any future circle overlay keep the original flat paint
        // (verbatim from pre-7a88b9a: white stroke halo for satellite contrast,
        // radius curve 6:2.5/11:4.5/14:7, stroke-width 1).
        const statusAware = o.key === 'substations' || o.key === 'dist';
        m.addLayer({
          id: `ovl-${o.key}`, source: `ovl-${o.key}`, type: 'circle',
          layout: { visibility: 'none' },
          paint: statusAware ? {
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 7, 3, 12, 6],
            'circle-color': [
              'case',
              ['all', ['==', ['coalesce', ['get', 'overlay'], false], true],
                      ['!=', ['coalesce', ['get', 'operational_status'], 'existing'], 'existing']],
              'rgba(0,0,0,0)',                                   // planned/approved/candidate = hollow
              o.color,
            ],
            'circle-stroke-width': 1.4,
            'circle-stroke-color': [
              'case',
              ['==', ['coalesce', ['get', 'operational_status'], 'existing'], 'candidate'],
              DISTRIBUTION_COLOR,                                 // unverified = muted ring
              o.color,
            ],
            'circle-opacity': 0.9,
          } : {
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 2.5, 11, 4.5, 14, 7],
            'circle-color': o.color,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 1,
            'circle-opacity': 0.9,
          },
        });
      }
    }

    // Client LRM streams — installed here with the other context overlays, so
    // the flowlines sit UNDER the buildable highlight, the pipelines, the
    // connector, and the dots. Two line layers over one source because
    // line-dasharray isn't data-drivable: Perennial + Stream/River solid,
    // everything else (Intermittent / Ephemeral) dashed. Gated so callers that
    // don't offer the row get no source and no layers at all.
    if (lrmStreamsAvailable) {
      m.addSource('portfolio-lrm-streams', { type: 'geojson', data: EMPTY_FC });
      m.addLayer({
        id: 'lrm-stream-perennial', source: 'portfolio-lrm-streams', type: 'line',
        filter: ['match', ['get', 'cls'], ['Perennial', 'Stream/River'], true, false],
        layout: { visibility: 'none', 'line-cap': 'round' },
        paint: {
          'line-color': STREAM_PER_COLOR,
          'line-width': 1.4,
          'line-opacity': isSat ? 0.9 : 0.7,
        },
      });
      m.addLayer({
        id: 'lrm-stream-intermittent', source: 'portfolio-lrm-streams', type: 'line',
        filter: ['!', ['match', ['get', 'cls'], ['Perennial', 'Stream/River'], true, false]],
        layout: { visibility: 'none', 'line-cap': 'round' },
        paint: {
          'line-color': STREAM_INT_COLOR,
          'line-width': 1.4,
          'line-dasharray': [2, 2],
          'line-opacity': isSat ? 0.9 : 0.7,
        },
      });
    }

    // Site footprint shapes — added FIRST so they render beneath the pipelines
    // and dots. Two source/layer pairs: the dissolved assembly outline (always
    // visible, the "Assemblies" default) and the member parcels (hidden until
    // the Parcels toggle). Fills sit at the bottom; both lines draw above the
    // fills so the block boundary + parcel edges stay crisp; dots stay on top
    // as the sole hover/click target. Fill color = knockout tone (see markerColor).
    m.addSource('portfolio-outlines', { type: 'geojson', data: EMPTY_FC });
    m.addSource('portfolio-parcels', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'portfolio-outline-fill',
      source: 'portfolio-outlines',
      type: 'fill',
      paint: { 'fill-color': ['get', 'fill'], 'fill-opacity': isSat ? 0.22 : 0.12 },
    });
    m.addLayer({
      id: 'portfolio-parcels-fill',
      source: 'portfolio-parcels',
      type: 'fill',
      layout: { visibility: 'none' },
      paint: { 'fill-color': ['get', 'fill'], 'fill-opacity': isSat ? 0.14 : 0.07 },
    });
    m.addLayer({
      id: 'portfolio-outline-line',
      source: 'portfolio-outlines',
      type: 'line',
      paint: {
        'line-color': ['get', 'fill'],
        'line-width': 1.4,
        'line-opacity': isSat ? 0.95 : 0.75,
      },
    });
    m.addLayer({
      id: 'portfolio-parcels-line',
      source: 'portfolio-parcels',
      type: 'line',
      layout: { visibility: 'none' },
      paint: {
        'line-color': ['get', 'fill'],
        'line-width': 0.7,
        'line-opacity': isSat ? 0.8 : 0.55,
      },
    });

    // Clean buildable pad — the largest contiguous block outside FEMA 100-yr
    // flood + NWI wetlands. Green fill above the outline/parcel fills so the
    // buildable core reads clearly; ON by default (see showCleanPad). Added
    // before pipelines/tracts/dots so those stay on top as hover targets.
    m.addSource('portfolio-clean-pad', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'portfolio-clean-pad-fill',
      source: 'portfolio-clean-pad',
      type: 'fill',
      paint: { 'fill-color': CLEANPAD_COLOR, 'fill-opacity': isSat ? 0.4 : 0.28 },
    });
    m.addLayer({
      id: 'portfolio-clean-pad-line',
      source: 'portfolio-clean-pad',
      type: 'line',
      paint: { 'line-color': CLEANPAD_COLOR, 'line-width': 1, 'line-opacity': isSat ? 0.95 : 0.7 },
    });
    // Hovering ONE buildable piece lifts just that piece — a brighter fill and
    // its own ring — so the cursor answers "which piece is this?" rather than
    // lighting up the whole site. Below the site ring so an active site still
    // reads as one shape. Filtered to nothing until a piece is hovered.
    m.addLayer({
      id: 'portfolio-clean-pad-hover-fill',
      source: 'portfolio-clean-pad',
      type: 'fill',
      paint: { 'fill-color': CLEANPAD_COLOR, 'fill-opacity': isSat ? 0.62 : 0.46 },
      filter: ['==', ['get', 'piece_id'], ''],
    });
    m.addLayer({
      id: 'portfolio-clean-pad-hover-line',
      source: 'portfolio-clean-pad',
      type: 'line',
      paint: { 'line-color': CLEANPAD_COLOR, 'line-width': 2, 'line-opacity': 0.95 },
      filter: ['==', ['get', 'piece_id'], ''],
    });
    // Highlight ring for the externally-selected / hovered pad (Crown Pine pad
    // mode). Same source, filtered to the active/hovered pad_id, so it covers
    // every piece of that site; inert in Integra mode (features carry no pad_id
    // → filter matches nothing).
    m.addLayer({
      id: 'portfolio-clean-pad-active',
      source: 'portfolio-clean-pad',
      type: 'line',
      paint: { 'line-color': REPORT.accent, 'line-width': 2.6, 'line-opacity': 0.95 },
      filter: ['==', ['get', 'pad_id'], ''],
    });
    // The screened campus placement, outline only — buildableHighlight mode's
    // "Screened placement" row. Empty + hidden unless that caller populates it,
    // so the research page gains nothing. Drawn above the buildable fill: it is
    // the plan sketched on top of the land, same grammar as the pad rectangles.
    m.addSource('portfolio-campus-placement', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'portfolio-campus-placement-line',
      source: 'portfolio-campus-placement',
      type: 'line',
      layout: { visibility: 'none' },
      paint: {
        'line-color': CP_RECT_COLOR,
        'line-width': 2,
        'line-dasharray': [3, 2],
        'line-opacity': isSat ? 0.95 : 0.85,
      },
    });

    // Pad acreage labels (Crown Pine pad mode only — Integra's block-form
    // features carry no `label`, so nothing renders there). One label per pad
    // polygon, zoom-gated so the overview stays clean.
    m.addLayer({
      id: 'portfolio-clean-pad-label',
      source: 'portfolio-clean-pad',
      type: 'symbol',
      minzoom: 9.5,
      layout: {
        'text-field': ['coalesce', ['get', 'label'], ''],
        'text-size': ['interpolate', ['linear'], ['zoom'], 9.5, 9.5, 13, 12],
        'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
      },
      paint: {
        'text-color': '#14532d',
        'text-halo-color': 'rgba(255,255,255,0.9)',
        'text-halo-width': 1.2,
      },
    });

    // Build-overable streambeds (NWI R4 riverine polygons). Installed ABOVE the
    // buildable fills on purpose: the point of the layer is to show the bed an
    // effective piece bridges, so it has to stay visible through the highlight.
    // Rendered as a line — the two banks of a narrow bed read as one ribbon.
    m.addSource('portfolio-streambeds', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: STREAMBED_LAYER_ID,
      source: 'portfolio-streambeds',
      type: 'line',
      layout: { visibility: 'none' },
      paint: {
        'line-color': STREAM_INT_COLOR,
        'line-width': 1,
        'line-dasharray': [1, 2],
        'line-opacity': isSat ? 0.95 : 0.8,
      },
    });

    // A1 acquisition premises — outline only, installed above the buildable
    // fills and the streambeds so the boundary reads over whichever basis is
    // drawn. Two line layers over one source: the analyzed core underneath as a
    // hairline, which shows through only along its shared edge with the western
    // band (everything else of it runs under the boundary line), and the
    // dissolved premises boundary on top.
    m.addSource('portfolio-a1-premises', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'a1-premises-division',
      source: 'portfolio-a1-premises',
      type: 'line',
      filter: ['==', ['get', 'part'], 'core'],
      layout: { visibility: 'none' },
      paint: {
        'line-color': isSat ? REPORT.white : REPORT.secondary,
        'line-width': 0.9,
        'line-opacity': isSat ? 0.8 : 0.6,
      },
    });
    m.addLayer({
      id: 'a1-premises-line',
      source: 'portfolio-a1-premises',
      type: 'line',
      filter: ['==', ['get', 'part'], 'premises'],
      layout: { visibility: 'none' },
      paint: {
        'line-color': isSat ? REPORT.white : REPORT.heading,
        'line-width': 2,
        'line-opacity': isSat ? 0.95 : 0.9,
      },
    });

    // Crown Pine pad rectangles — dashed plan-view outlines + acreage/verdict
    // labels, above the clean-pad fills. Fetched once by loadCpRects.
    m.addSource('portfolio-cp-rects', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'cp-rect-line', source: 'portfolio-cp-rects', type: 'line',
      layout: { visibility: 'none' },
      paint: { 'line-color': CP_RECT_COLOR, 'line-width': 2.2, 'line-dasharray': [3, 2], 'line-opacity': 0.95 },
    });
    m.addLayer({
      id: 'cp-rect-label', source: 'portfolio-cp-rects', type: 'symbol',
      minzoom: 8.5,
      layout: {
        visibility: 'none',
        'text-field': ['get', 'label'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 8.5, 9.5, 13, 12.5],
        'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
      },
      paint: {
        'text-color': CP_RECT_COLOR,
        'text-halo-color': 'rgba(255,255,255,0.92)',
        'text-halo-width': 1.3,
      },
    });

    // Site A & C streams — NHD flowlines classed perennial/intermittent.
    // Off-site context (1-mi ring) draws dimmed via the on_site flag.
    // Fetched once by loadSiteStreams.
    m.addSource('portfolio-site-streams', { type: 'geojson', data: EMPTY_FC });
    const streamOpacity: mapboxgl.Expression = ['case', ['get', 'on_site'], 0.95, 0.45];
    m.addLayer({
      id: 'ss-per-line', source: 'portfolio-site-streams', type: 'line',
      filter: ['==', ['get', 'class'], 'Perennial'],
      layout: { visibility: 'none', 'line-cap': 'round' },
      paint: {
        'line-color': STREAM_PER_COLOR,
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1.2, 12, 2.4, 14, 3.2],
        'line-opacity': streamOpacity,
      },
    });
    m.addLayer({
      id: 'ss-int-line', source: 'portfolio-site-streams', type: 'line',
      filter: ['!=', ['get', 'class'], 'Perennial'],
      layout: { visibility: 'none' },
      paint: {
        'line-color': STREAM_INT_COLOR,
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.9, 12, 1.7, 14, 2.4],
        'line-dasharray': [2, 2],
        'line-opacity': streamOpacity,
      },
    });
    m.addLayer({
      id: 'ss-name-label', source: 'portfolio-site-streams', type: 'symbol',
      filter: ['==', ['get', 'named'], true],
      minzoom: 10,
      layout: {
        visibility: 'none',
        'symbol-placement': 'line',
        'text-field': ['get', 'name'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 10, 9.5, 14, 12],
        'text-font': ['DIN Pro Italic', 'Arial Unicode MS Regular'],
      },
      paint: {
        'text-color': '#075985',
        'text-halo-color': 'rgba(255,255,255,0.92)',
        'text-halo-width': 1.3,
      },
    });

    m.addSource('portfolio-pipelines', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'portfolio-pipelines',
      source: 'portfolio-pipelines',
      type: 'line',
      // Family styling (client presentation) draws the two screened operator
      // families and hides everything else; the research page keeps every line
      // in one neutral ink. `operator_parent` is served by /api/pipelines/geojson.
      // Hiding is done with a FILTER, not a transparent paint: a transparent
      // feature is still hit-testable, so paint-only hiding would pop operator
      // tooltips over lines nobody can see.
      ...(pipelineFamilyStyling ? { filter: PIPELINE_FAMILY_FILTER } : {}),
      paint: pipelineFamilyStyling
        ? {
            // The match's fallback arm is unreachable under the filter above —
            // `match` simply requires one.
            'line-color': ['match', ['get', 'operator_parent'],
              'Energy Transfer', ET_PIPELINE_COLOR,
              'Kinder Morgan', KM_PIPELINE_COLOR,
              'rgba(0,0,0,0)'] as mapboxgl.Expression,
            // ['zoom'] is only legal at the top level of an interpolate/step —
            // nesting it inside a match gets the whole layer rejected. Same
            // widths as before, inverted so zoom is outermost.
            'line-width': ['interpolate', ['linear'], ['zoom'],
              5, ['match', ['get', 'operator_parent'], 'Energy Transfer', 1.4, 'Kinder Morgan', 0.3, 0.4],
              10, ['match', ['get', 'operator_parent'], 'Energy Transfer', 2.2, 'Kinder Morgan', 0.9, 1.2],
              14, ['match', ['get', 'operator_parent'], 'Energy Transfer', 3.2, 'Kinder Morgan', 1.6, 2.2]] as mapboxgl.Expression,
            'line-opacity': isSat ? 0.95 : 0.85,
          }
        : {
            'line-color': isSat ? '#FBE7C6' : REPORT.tertiary,
            'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0.4, 10, 1.2, 14, 2.2],
            'line-opacity': isSat ? 0.9 : 0.55,
          },
    });
    // Invisible hit strip directly above the drawn line. The painted pipeline is
    // 0.4–2.2px wide, which gives a ~2px hover target nobody can land on; this
    // carries the hover instead at a usable 12px. Same source and same family
    // filter, so it can never make a hidden operator hoverable.
    m.addLayer({
      id: PIPELINE_HIT_LAYER,
      source: 'portfolio-pipelines',
      type: 'line',
      ...(pipelineFamilyStyling ? { filter: PIPELINE_FAMILY_FILTER } : {}),
      paint: { 'line-opacity': 0, 'line-width': 12 },
    });

    // Screened campus-to-tap connector for the ACTIVE site. One source, two
    // filtered layers: the run on supplied Crown Pine inventory is solid, the
    // run outside it is dashed at the same width. Empty until a caller supplies
    // geometry, so the research page is untouched.
    m.addSource('portfolio-connector', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'portfolio-connector-cp',
      source: 'portfolio-connector',
      type: 'line',
      filter: ['==', ['get', 'segment'], 'cp'],
      paint: {
        'line-color': CONNECTOR_CP_COLOR,
        'line-width': 2.4,
        'line-opacity': 0.95,
      },
    });
    m.addLayer({
      id: 'portfolio-connector-outside',
      source: 'portfolio-connector',
      type: 'line',
      filter: ['==', ['get', 'segment'], 'outside'],
      paint: {
        'line-color': CONNECTOR_OUTSIDE_COLOR,
        'line-width': 2.4,
        'line-opacity': 0.95,
        'line-dasharray': [2, 1.6],
      },
    });

    m.addSource('portfolio-tracts', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'portfolio-tracts-halo',
      source: 'portfolio-tracts',
      type: 'circle',
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 8, 10, 14, 14, 20],
        'circle-color': ['get', 'fill'],
        'circle-opacity': isSat ? 0.32 : 0.18,
        'circle-stroke-width': 0,
      },
    });
    // Active-selection accent ring — wider than the halo, accent-colored,
    // filtered to the externally-driven activeParcelId. Renders BELOW the dot.
    m.addLayer({
      id: 'portfolio-tracts-active-ring',
      source: 'portfolio-tracts',
      type: 'circle',
      filter: ['==', ['get', 'parcel_id'], ''],
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 16, 10, 22, 14, 30],
        'circle-color': 'transparent',
        'circle-stroke-color': REPORT.accent,
        'circle-stroke-width': 2.5,
        'circle-stroke-opacity': 0.9,
      },
    });
    m.addLayer({
      id: 'portfolio-tracts-dot',
      source: 'portfolio-tracts',
      type: 'circle',
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 3.5, 10, 6, 14, 9],
        'circle-color': ['get', 'fill'],
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 1.2,
      },
    });

    m.on('mouseenter', 'portfolio-tracts-dot', (e) => {
      m.getCanvas().style.cursor = 'pointer';
      const f = e.features?.[0];
      if (!f) return;
      const p = f.properties as Record<string, string>;
      setHoverParcel(p.parcel_id);
      onHoverRef.current?.(p.parcel_id);
      const coords = (f.geometry as GeoJSON.Point).coordinates as [number, number];
      if (popupRef.current) popupRef.current.remove();
      popupRef.current = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 12,
        className: 'portfolio-map-popup',
      })
        .setLngLat(coords)
        .setHTML(`
          <div style="font-family: ${PORTFOLIO_TOKENS.sans}; font-size: 11px; line-height: 1.4; color: ${REPORT.body}; min-width: 200px;">
            <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 8.5px; letter-spacing: 0.08em; color: ${REPORT.tertiary}; text-transform: uppercase;">
              Rank ${p.rank || '—'} · ${p.state}
            </div>
            <div style="font-family: ${PORTFOLIO_TOKENS.serif}; font-size: 14px; color: ${REPORT.heading}; margin-top: 2px;">
              ${p.county} County${p.site_label ? ` <span style="color:${REPORT.secondary};font-size:12px;">${p.site_label}</span>` : ''}
            </div>
            <div style="margin-top: 4px; display: flex; gap: 6px; align-items: center; font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 9.5px; color: ${REPORT.secondary};">
              <span style="color: ${REPORT.heading}; font-weight: 600;">${p.mw || '—'} MW</span>
              <span>·</span>
              <span>${p.acres || '—'} ac</span>
              <span>·</span>
              <span style="color: ${REPORT.heading}; font-weight: 600;">Grade ${p.grade || '—'}</span>
            </div>
            ${p.gates ? `<div style="margin-top: 4px; font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 8.5px; letter-spacing: 0.04em; color: ${toneStyle('fail').fg};">Trips: ${p.gates}</div>` : ''}
          </div>
        `)
        .addTo(m);
    });
    m.on('mouseleave', 'portfolio-tracts-dot', () => {
      m.getCanvas().style.cursor = '';
      if (popupRef.current) { popupRef.current.remove(); popupRef.current = null; }
      setHoverParcel(null);
      onHoverRef.current?.(null);
    });
    m.on('click', 'portfolio-tracts-dot', (e) => {
      if (measureActiveRef.current) return;
      const f = e.features?.[0];
      if (!f) return;
      const pid = (f.properties as { parcel_id?: string })?.parcel_id;
      if (pid) onSelectRef.current?.(pid);
    });

    // All-CP owner-parcel hover — individual parcel acreage + slope (Crown Pine
    // research mode). Follows the mouse; only fires when the layer is visible.
    m.on('mousemove', 'portfolio-owner-parcels-fill', (e) => {
      m.getCanvas().style.cursor = 'pointer';
      const f = e.features?.[0];
      if (!f) return;
      const p = f.properties as Record<string, string>;
      const ac = p.acreage != null && p.acreage !== '' ? `${Number(p.acreage).toLocaleString()} ac` : '—';
      const slope = p.avg_slope_pct != null && p.avg_slope_pct !== ''
        ? `${Number(p.avg_slope_pct).toFixed(1)}%` : 'n/a';
      if (ownerPopupRef.current) ownerPopupRef.current.remove();
      ownerPopupRef.current = new mapboxgl.Popup({
        closeButton: false, closeOnClick: false, offset: 8, className: 'portfolio-map-popup',
      })
        .setLngLat(e.lngLat)
        .setHTML(`
          <div style="font-family: ${PORTFOLIO_TOKENS.sans}; font-size: 11px; line-height: 1.45; color: ${REPORT.body}; min-width: 150px;">
            <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 8.5px; letter-spacing: 0.06em; color: ${REPORT.tertiary}; text-transform: uppercase;">
              ${escText(p.owner_name || 'Crown Pine')} · ${escText(p.county || '')}
            </div>
            <div style="margin-top: 3px; display: flex; gap: 8px; font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 10px; color: ${REPORT.secondary};">
              <span style="color: ${REPORT.heading}; font-weight: 600;">${ac}</span>
              <span>·</span>
              <span>Slope ${slope}</span>
            </div>
          </div>
        `)
        .addTo(m);
    });
    m.on('mouseleave', 'portfolio-owner-parcels-fill', () => {
      m.getCanvas().style.cursor = '';
      if (ownerPopupRef.current) { ownerPopupRef.current.remove(); ownerPopupRef.current = null; }
    });

    // Crown Pine LRM tract hover — the seller's tract name / legal acres / fund.
    const escText = (s: unknown) => String(s ?? '').replace(/[&<>"']/g, c =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
    m.on('mousemove', 'cp-tract-fill', (e) => {
      if (measureActiveRef.current) return;
      m.getCanvas().style.cursor = 'pointer';
      const f = e.features?.[0];
      if (!f) return;
      const p = f.properties as Record<string, string>;
      const ac = p.legal_acres != null && p.legal_acres !== '' ? `${Number(p.legal_acres).toLocaleString()} ac` : '— ac';
      const fund = String(p.fund || '').split('-')[0] || '—';
      const group = p.tract_group && p.tract_group !== 'Crown Pine' ? ` · ${escText(p.tract_group)}` : '';
      if (ownerPopupRef.current) ownerPopupRef.current.remove();
      ownerPopupRef.current = new mapboxgl.Popup({
        closeButton: false, closeOnClick: false, offset: 8, className: 'portfolio-map-popup',
      })
        .setLngLat(e.lngLat)
        .setHTML(`
          <div style="font-family: ${PORTFOLIO_TOKENS.sans}; font-size: 11px; line-height: 1.45; color: ${REPORT.body}; min-width: 150px;">
            <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 8.5px; letter-spacing: 0.06em; color: ${REPORT.tertiary}; text-transform: uppercase;">
              Crown Pine tract · ${escText(fund)}${group}
            </div>
            <div style="margin-top: 3px; display: flex; gap: 8px; font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 10px; color: ${REPORT.secondary};">
              <span style="color: ${REPORT.heading}; font-weight: 600;">${escText(p.tract_name)}</span>
              <span>·</span>
              <span>${ac}</span>
            </div>
          </div>
        `)
        .addTo(m);
    });
    m.on('mouseleave', 'cp-tract-fill', () => {
      m.getCanvas().style.cursor = '';
      if (ownerPopupRef.current) { ownerPopupRef.current.remove(); ownerPopupRef.current = null; }
    });

    // County CAD fabric hover — the owner of record, county and CAD acreage.
    // This is the layer that answers "who owns the gap inside this block?", so
    // the owner name leads. Values come from county appraisal rolls → escape
    // every interpolation.
    m.on('mousemove', 'portfolio-cad-fabric-fill', (e) => {
      if (measureActiveRef.current) return;
      const f = e.features?.[0];
      if (!f) return;
      const p = f.properties as Record<string, string>;
      const ac = p.acreage != null && p.acreage !== '' ? `${Number(p.acreage).toLocaleString()} ac` : '— ac';
      m.getCanvas().style.cursor = 'pointer';
      if (cadPopupRef.current) cadPopupRef.current.remove();
      cadPopupRef.current = new mapboxgl.Popup({
        closeButton: false, closeOnClick: false, offset: 8, className: 'portfolio-map-popup',
      })
        .setLngLat(e.lngLat)
        .setHTML(`
          <div style="font-family: ${PORTFOLIO_TOKENS.sans}; font-size: 11px; line-height: 1.45; color: ${REPORT.body}; min-width: 150px; max-width: 240px;">
            <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 8.5px; letter-spacing: 0.06em; color: ${REPORT.tertiary}; text-transform: uppercase;">
              Owner of record${p.county ? ` · ${escText(p.county)}` : ''}
            </div>
            <div style="margin-top: 3px; color: ${REPORT.heading}; font-weight: 600;">${escText(p.owner_name || '(unknown)')}</div>
            <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 10px; color: ${REPORT.secondary};">${ac}</div>
          </div>
        `)
        .addTo(m);
    });
    m.on('mouseleave', 'portfolio-cad-fabric-fill', () => {
      m.getCanvas().style.cursor = '';
      if (cadPopupRef.current) { cadPopupRef.current.remove(); cadPopupRef.current = null; }
    });

    // Crown Pine pad hover / click — the pad's OWN acreage + slope, and select.
    // padModeRef gates these OFF for Integra (its clean-pad features carry no
    // pad_id), so the Integra map behaves exactly as before.
    m.on('mousemove', 'portfolio-clean-pad-fill', (e) => {
      if (!padModeRef.current) return;
      m.getCanvas().style.cursor = 'pointer';
      const f = e.features?.[0];
      if (!f) return;
      const p = f.properties as Record<string, string>;
      onHoverPadRef.current?.(p.pad_id || null);
      setHoveredPieceId(p.piece_id || null);
      const ac = p.acres != null && p.acres !== '' ? `${Number(p.acres).toLocaleString()} ac` : '—';
      const slope = p.slope != null && p.slope !== '' ? `${Number(p.slope).toFixed(1)}%` : 'n/a';
      // Slope is measured on the screened placement, not on a buildable piece —
      // so a piece popup states its acreage and stops there.
      const slopeLine = p.piece ? '' : `
              <span>·</span>
              <span>Slope ${slope}</span>`;
      const onEffective = p.basis === 'effective';
      const eyebrow = p.piece
        ? (onEffective ? 'Buildable · effective' : 'Buildable acreage')
        : p.shortlist ? 'Selected campus screen' : 'Buildable pad';
      // An effective piece is several screened pieces rejoined; say how many
      // crossings that took, so the bigger shape carries its own explanation.
      const crossings = onEffective && Number(p.crossings) > 0 ? `
            <div style="margin-top: 2px; font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 9.5px; color: ${REPORT.tertiary};">${Number(p.crossings)} streambed crossing${Number(p.crossings) === 1 ? '' : 's'}</div>` : '';
      // The map paints no piece acreage any more, so the popup carries both the
      // number and which site the piece belongs to.
      const blockLine = p.block_id ? `
            <div style="margin-top: 2px; font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 9.5px; color: ${REPORT.tertiary};">${esc(p.block_id)}</div>` : '';
      const pendingLine = p.flood_effective_date ? `<div style="margin-top: 3px; color: ${PENDING_FEMA_COLOR};">${esc(pendingFemaLabel(p.flood_effective_date))}</div>` : '';
      if (padPopupRef.current) padPopupRef.current.remove();
      padPopupRef.current = new mapboxgl.Popup({
        closeButton: false, closeOnClick: false, offset: 8, className: 'portfolio-map-popup',
      })
        .setLngLat(e.lngLat)
        .setHTML(`
          <div style="font-family: ${PORTFOLIO_TOKENS.sans}; font-size: 11px; line-height: 1.45; color: ${REPORT.body}; min-width: 130px;">
            <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 8.5px; letter-spacing: 0.06em; color: ${REPORT.tertiary}; text-transform: uppercase;">${eyebrow}</div>
            <div style="margin-top: 3px; display: flex; gap: 8px; font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 10px; color: ${REPORT.secondary};">
              <span style="color: ${REPORT.heading}; font-weight: 600;">${ac}</span>${slopeLine}
            </div>${crossings}${blockLine}${pendingLine}
          </div>
        `)
        .addTo(m);
    });
    m.on('mouseleave', 'portfolio-clean-pad-fill', () => {
      if (!padModeRef.current) return;
      m.getCanvas().style.cursor = '';
      if (padPopupRef.current) { padPopupRef.current.remove(); padPopupRef.current = null; }
      onHoverPadRef.current?.(null);
      setHoveredPieceId(null);
    });
    m.on('click', 'portfolio-clean-pad-fill', (e) => {
      if (measureActiveRef.current) return;
      if (!padModeRef.current) return;
      const f = e.features?.[0];
      if (!f) return;
      const padId = (f.properties as { pad_id?: string })?.pad_id;
      if (padId) onSelectPadRef.current?.(padId);
    });

    // Provenance popup for power overlay features (click, not hover — hover
    // is owned by the parcel/pad popups). Feature properties are attacker-
    // controllable (ingested from external sources), so every interpolated
    // string is escaped before landing in setHTML.
    const esc = (s: unknown) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const onPowerClick = (e: mapboxgl.MapLayerMouseEvent) => {
      if (measureActiveRef.current) return;
      const f = e.features?.[0];
      if (!f) return;
      const p: any = f.properties || {};
      const conf = p.overlay ? `${esc(p.attribute_confidence)} attrs / ${esc(p.geometry_confidence)} location` : 'HIFLD baseline';
      const src = typeof p.source_url === 'string' && /^https?:\/\//i.test(p.source_url)
        ? `<a href="${esc(p.source_url)}" target="_blank" rel="noreferrer" style="color:#2563eb">source</a>`
        : '';
      // HIFLD redacts many owner/name fields as "NOT AVAILABLE" — don't echo it.
      const nm = isRedacted(p.name) ? '(unnamed)' : esc(p.name);
      const op = isRedacted(p.operator) ? 'Owner not published (HIFLD)' : esc(p.operator);
      new mapboxgl.Popup({ closeButton: true, maxWidth: '260px' })
        .setLngLat(e.lngLat)
        .setHTML(`<div style="font:11px/1.45 -apple-system,sans-serif">
          <strong>${nm}</strong><br/>
          ${op} · ${p.voltage_kv ? esc(p.voltage_kv) + ' kV' : '— kV'}<br/>
          ${esc(p.operational_status) || 'existing'} · ${conf} ${src}</div>`)
        .addTo(m);
    };
    // Hover-spec popup shared by the line layers — transmission (voltage /
    // operator) and the always-on gas transmission pipelines (operator /
    // diameter / MW). Follows the cursor; suppressed while measuring.
    const showLineSpecs = (lngLat: mapboxgl.LngLat, html: string) => {
      m.getCanvas().style.cursor = 'pointer';
      if (linePopupRef.current) linePopupRef.current.remove();
      linePopupRef.current = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 8, className: 'portfolio-map-popup' })
        .setLngLat(lngLat).setHTML(html).addTo(m);
    };
    const hideLineSpecs = () => {
      m.getCanvas().style.cursor = '';
      if (linePopupRef.current) { linePopupRef.current.remove(); linePopupRef.current = null; }
    };
    // HIFLD redacts owner/name on many transmission lines — the raw values come
    // through as "NOT AVAILABLE" (or "TAP…- NOT AVAILABLE"). Don't echo that
    // string: treat it as unpublished. Lead with kV (the one hard fact) when the
    // name is redacted, and label the missing owner clearly.
    const isRedacted = (s: unknown) => !s || /not available/i.test(String(s));
    const txSpecsHTML = (p: any) => {
      const kv = p.voltage_kv ? `${esc(p.voltage_kv)} kV` : '— kV';
      const nameOk = !isRedacted(p.name);
      const owner = isRedacted(p.operator) ? 'Owner not published (HIFLD)' : esc(p.operator);
      const lead = nameOk ? esc(p.name) : kv;
      const sub = nameOk ? `${owner} · ${kv}` : owner;
      return `
      <div style="font-family: ${PORTFOLIO_TOKENS.sans}; font-size: 11px; line-height: 1.45; color: ${REPORT.body}; min-width: 150px;">
        <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 8.5px; letter-spacing: 0.06em; color: ${REPORT.tertiary}; text-transform: uppercase;">Transmission line</div>
        <div style="margin-top: 3px; color: ${REPORT.heading}; font-weight: 600;">${lead}</div>
        <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 10px; color: ${REPORT.secondary};">${sub}</div>
      </div>`;
    };
    // Airport hover — name / class / runway / distance. Points come from
    // /api/crownpine-parcels/airports (AOI-local ∪ nearest large-commercial).
    const airportSpecsHTML = (p: any) => {
      const cls = p.airport_class ? String(p.airport_class).replace(/_/g, ' ') : 'airport';
      const rw = p.runway_ft ? `${Number(p.runway_ft).toLocaleString()} ft runway` : 'runway n/a';
      const mi = p.mi != null && p.mi !== '' ? ` · ${Number(p.mi).toFixed(0)} mi` : '';
      const note = p.beyond_aoi ? `<div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 9px; color: ${REPORT.tertiary}; margin-top: 2px;">nearest large-commercial</div>` : '';
      return `
      <div style="font-family: ${PORTFOLIO_TOKENS.sans}; font-size: 11px; line-height: 1.45; color: ${REPORT.body}; min-width: 150px;">
        <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 8.5px; letter-spacing: 0.06em; color: ${REPORT.tertiary}; text-transform: uppercase;">Airport</div>
        <div style="margin-top: 3px; color: ${REPORT.heading}; font-weight: 600;">${esc(p.name) || '(unnamed)'}</div>
        <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 10px; color: ${REPORT.secondary};">${esc(cls)} · ${rw}${mi}</div>
        ${note}
      </div>`;
    };
    // Gas-pipeline hover. Leads with the OPERATOR — "who owns this line?" is
    // the first question anyone asks of a line on the map, and the line's own
    // name is often a bare RRC system id. Every spec row is null-safe and is
    // OMITTED when its value is missing rather than printed as an em dash, so
    // a thin RRC record yields a two-line tooltip instead of a wall of blanks.
    // Values are ingested from RRC / EIA → esc() every interpolation.
    const pipeSpecsHTML = (p: any) => {
      const num = (v: unknown) => {
        const n = Number(v);
        return v != null && v !== '' && Number.isFinite(n) ? n : null;
      };
      // The API substitutes the literal 'Unknown' when the record carries no
      // operator — read that as missing, not as a company name.
      const operatorRaw = String(p.operator ?? '').trim();
      const operator = operatorRaw && !/^unknown$/i.test(operatorRaw) ? operatorRaw : '';
      const parentRaw = String(p.operator_parent ?? '').trim();
      // The family suffix only earns its place when it names something new —
      // not a case/whitespace variant, and not a containment pair like
      // "Kinder Morgan Texas Pipeline" under "Kinder Morgan".
      const opLower = operator.toLowerCase();
      const parLower = parentRaw.toLowerCase();
      const family = parentRaw && operator && parLower !== opLower
        && !opLower.includes(parLower) && !parLower.includes(opLower)
        ? `<span style="color: ${REPORT.tertiary}; font-weight: 400;"> · ${esc(parentRaw)} family</span>`
        : '';
      const lineName = String(p.display_name ?? '').trim() || String(p.name ?? '').trim();
      const nameLine = lineName
        ? `<div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 10px; color: ${REPORT.secondary};">${esc(lineName)}</div>`
        : '';

      const specs: string[] = [];
      const dia = num(p.diameter_inches);
      if (dia != null) specs.push(`${esc(dia)}″`);
      const capacity = pipelineCapacityDisplay(p);
      const cap = capacity.capacityMmcf;
      if (cap != null) specs.push(`${esc(capacity.label)}: ${esc(Math.round(cap).toLocaleString())} MMcf/d`);
      else specs.push('Segment capacity unavailable');
      const util = num(p.utilization_pct);
      if (util != null) {
        // A missing or 'modeled' source means the intrastate default, not an
        // observed number — label it so nobody reads a 50% default as an EBB
        // posting. Anything else came from observed flow data and stands plain.
        const usrc = String(p.utilization_source ?? '').trim();
        const qualifier = !usrc || /^modeled$/i.test(usrc) ? ' (modeled)' : '';
        specs.push(`${esc(Math.round(util))}% utilized${qualifier}`);
      }
      const mw = capacity.powerMw;
      if (mw != null) specs.push(`~${esc(Math.round(mw).toLocaleString())} MW power screening equivalent`);
      const specLine = specs.length
        ? `<div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 10px; color: ${REPORT.secondary}; margin-top: 2px;">${specs.join(' · ')}</div>`
        : '';

      const sourceRaw = String(p.source ?? '').trim();
      const sourceLine = sourceRaw
        ? `<div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 9px; color: ${REPORT.tertiary}; margin-top: 3px;">${esc(sourceRaw.length <= 5 ? sourceRaw.toUpperCase() : sourceRaw)}</div>`
        : '';

      return `
        <div style="font-family: ${PORTFOLIO_TOKENS.sans}; font-size: 11px; line-height: 1.45; color: ${REPORT.body}; min-width: 170px; max-width: 250px;">
          <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 8.5px; letter-spacing: 0.06em; color: ${REPORT.tertiary}; text-transform: uppercase;">Gas transmission pipeline</div>
          <div style="margin-top: 3px; color: ${REPORT.heading}; font-weight: 600;">${operator ? esc(operator) : 'Unknown operator'}${family}</div>
          ${nameLine}${specLine}${sourceLine}
        </div>`;
    };

    // Connector hover — the screened route must never be read as a surveyed
    // one, so the caveat travels with the geometry. Installed unconditionally;
    // the layers are empty unless a caller passes `connector`.
    const connectorHTML = (p: { label?: string; detail?: string }) => `
      <div style="font-family: ${PORTFOLIO_TOKENS.sans}; font-size: 11px; line-height: 1.45; color: ${REPORT.body}; max-width: 250px;">
        <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 8.5px; letter-spacing: 0.06em; color: ${REPORT.tertiary}; text-transform: uppercase;">Screened connector</div>
        ${p.label ? `<div style="margin-top: 3px; color: ${REPORT.heading}; font-weight: 600;">${esc(p.label)}</div>` : ''}
        ${p.detail ? `<div style="font-size: 10px; color: ${REPORT.body}; margin-top: 4px; white-space: pre-line;">${esc(p.detail)}</div>` : ''}
        <div style="font-size: 10px; color: ${REPORT.secondary}; margin-top: 3px;">${CONNECTOR_CAVEAT}</div>
      </div>`;
    for (const lid of ['portfolio-connector-cp', 'portfolio-connector-outside']) {
      m.on('mousemove', lid, (e) => {
        if (measureActiveRef.current) return;
        showLineSpecs(e.lngLat, connectorHTML((e.features?.[0]?.properties || {}) as { label?: string; detail?: string }));
      });
      m.on('mouseleave', lid, hideLineSpecs);
    }

    // Airport hover (name / class / runway / distance). Installed for every
    // caller — the layer is empty until an Airports row turns it on, and both
    // the research map and the client map offer that row.
    m.on('mousemove', 'ovl-airports', (e) => { if (measureActiveRef.current) return; const f = e.features?.[0]; if (f) showLineSpecs(e.lngLat, airportSpecsHTML(f.properties || {})); });
    m.on('mouseleave', 'ovl-airports', hideLineSpecs);

    // Client LRM stream hover. Installed only where the layers exist, and
    // suppressed while measuring, like the connector + airport handlers above.
    // Names come from NHD → esc() them. The caveat travels with the geometry:
    // this is a screen, never a delineation.
    if (lrmStreamsAvailable) {
      const lrmStreamHTML = (p: any) => `
        <div style="font-family: ${PORTFOLIO_TOKENS.sans}; font-size: 11px; line-height: 1.45; color: ${REPORT.body}; max-width: 240px;">
          <div style="color: ${REPORT.heading}; font-weight: 600;">${esc(p.name) || 'Unnamed drainage'}</div>
          <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 10px; color: ${REPORT.secondary};">${esc(p.cls)} · NHDPlus HR flowline</div>
          <div style="font-size: 10px; color: ${REPORT.secondary}; margin-top: 3px;">Screening layer — not a delineation or jurisdictional determination.</div>
        </div>`;
      for (const lid of LRM_STREAM_LAYER_IDS) {
        m.on('mousemove', lid, (e) => { if (measureActiveRef.current) return; const f = e.features?.[0]; if (f) showLineSpecs(e.lngLat, lrmStreamHTML(f.properties || {})); });
        m.on('mouseleave', lid, hideLineSpecs);
      }
    }

    // Power popups install wherever the power overlay can be turned on: research
    // mode owns those rows, and any client map listing 'power' in clientOverlays
    // offers them too. Without this the client's lines and substations would draw
    // with no way to ask what they are. Layers stay empty until a row is on.
    if (powerPopups) {
      // Click → provenance popup (substations, dist yards, transmission lines).
      for (const lid of ['ovl-substations', 'ovl-dist', 'ovl-transmission', 'ovl-transmission-planned']) {
        m.on('click', lid, onPowerClick);
      }
      // Hover → spec popup for transmission lines.
      for (const lid of ['ovl-transmission', 'ovl-transmission-planned']) {
        m.on('mousemove', lid, (e) => { if (measureActiveRef.current) return; const f = e.features?.[0]; if (f) showLineSpecs(e.lngLat, txSpecsHTML(f.properties || {})); });
        m.on('mouseleave', lid, hideLineSpecs);
      }
    }

    // Gas-pipeline hover — bound for EVERY caller (it used to sit inside the
    // research-only block below, which left the client map with no way to ask
    // what a line was). The target is the wide invisible hit strip, not the
    // hairline itself.
    m.on('mousemove', PIPELINE_HIT_LAYER, (e) => { if (measureActiveRef.current) return; const f = e.features?.[0]; if (f) showLineSpecs(e.lngLat, pipeSpecsHTML(f.properties || {})); });
    m.on('mouseleave', PIPELINE_HIT_LAYER, hideLineSpecs);

    // ownerParcels gates the rest OFF for the client maps (which never pass it)
    // — those layers only exist in Crown Pine research mode, so their handlers
    // must be inert elsewhere. Mirrors the pad-mode gating above.
    if (ownerParcels) {
      // Other-owner hovers — tract (owner + screen verdict + pipeline), pad
      // (acres + slope, parity with the CP pad popup), and the 500-ac
      // rectangle outline. Owner names come from county records → esc() all.
      const ooTractHTML = (p: any) => {
        const ac = p.total_acres != null && p.total_acres !== '' ? `${Number(p.total_acres).toLocaleString()} ac` : '— ac';
        const pipe = p.pipeline
          ? `<div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 9px; color: ${REPORT.tertiary}; margin-top: 2px;">${esc(p.pipeline)}${p.pipeline_mi != null && p.pipeline_mi !== '' ? ` · ${Number(p.pipeline_mi).toFixed(1)} mi` : ''}</div>`
          : '';
        return `
        <div style="font-family: ${PORTFOLIO_TOKENS.sans}; font-size: 11px; line-height: 1.45; color: ${REPORT.body}; min-width: 170px;">
          <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 8.5px; letter-spacing: 0.06em; color: ${REPORT.tertiary}; text-transform: uppercase;">Other owner · ${esc(p.county) || ''}</div>
          <div style="margin-top: 3px; color: ${REPORT.heading}; font-weight: 600;">${esc(p.owner_name) || '(unknown)'}</div>
          <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 10px; color: ${REPORT.secondary};">${ac} · ${esc(p.parcel_count)} parcels</div>
          <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 9.5px; color: ${REPORT.secondary}; margin-top: 2px;">${esc(p.note)}</div>
          ${pipe}
        </div>`;
      };
      const ooPadHTML = (p: any) => {
        const ac = p.acres != null && p.acres !== '' ? `${Number(p.acres).toLocaleString()} ac` : '—';
        const slope = p.avg_slope_pct != null && p.avg_slope_pct !== '' ? `${Number(p.avg_slope_pct).toFixed(1)}%` : 'n/a';
        return `
        <div style="font-family: ${PORTFOLIO_TOKENS.sans}; font-size: 11px; line-height: 1.45; color: ${REPORT.body}; min-width: 150px;">
          <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 8.5px; letter-spacing: 0.06em; color: ${REPORT.tertiary}; text-transform: uppercase;">Buildable pad · ${esc(p.owner_name)}</div>
          <div style="margin-top: 3px; display: flex; gap: 8px; font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 10px; color: ${REPORT.secondary};">
            <span style="color: ${REPORT.heading}; font-weight: 600;">${ac}</span>
            <span>·</span>
            <span>Slope ${slope}</span>
          </div>
        </div>`;
      };
      const ooRectHTML = (p: any) => `
        <div style="font-family: ${PORTFOLIO_TOKENS.sans}; font-size: 11px; line-height: 1.45; color: ${REPORT.body}; min-width: 150px;">
          <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 8.5px; letter-spacing: 0.06em; color: ${REPORT.tertiary}; text-transform: uppercase;">500-ac screen rectangle</div>
          <div style="margin-top: 3px; color: ${REPORT.heading}; font-weight: 600;">${p.acres != null ? Number(p.acres).toLocaleString() : '—'} ac</div>
          <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 10px; color: ${REPORT.secondary};">${esc(p.rect_kind)} · ${esc(p.owner_name)}</div>
        </div>`;
      m.on('mousemove', 'oo-tract-fill', (e) => {
        if (measureActiveRef.current) return;
        // Pads render above the tract wash — let the pad popup own that hover.
        if (m.queryRenderedFeatures(e.point, { layers: ['oo-pad-fill'] }).length) return;
        const f = e.features?.[0];
        if (f) showLineSpecs(e.lngLat, ooTractHTML(f.properties || {}));
      });
      m.on('mouseleave', 'oo-tract-fill', hideLineSpecs);
      m.on('mousemove', 'oo-pad-fill', (e) => { if (measureActiveRef.current) return; const f = e.features?.[0]; if (f) showLineSpecs(e.lngLat, ooPadHTML(f.properties || {})); });
      m.on('mouseleave', 'oo-pad-fill', hideLineSpecs);
      m.on('mousemove', 'oo-rect-line', (e) => { if (measureActiveRef.current) return; const f = e.features?.[0]; if (f) showLineSpecs(e.lngLat, ooRectHTML(f.properties || {})); });
      m.on('mouseleave', 'oo-rect-line', hideLineSpecs);

      // Crown Pine pad-rectangle hover — the placement's acreage + verdict.
      const cpRectHTML = (p: any) => `
        <div style="font-family: ${PORTFOLIO_TOKENS.sans}; font-size: 11px; line-height: 1.45; color: ${REPORT.body}; min-width: 150px;">
          <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 8.5px; letter-spacing: 0.06em; color: ${REPORT.tertiary}; text-transform: uppercase;">Pad rectangle${p.site ? ` · Site ${esc(p.site)}` : ''}</div>
          <div style="margin-top: 3px; color: ${REPORT.heading}; font-weight: 600;">${esc(p.label)}</div>
          <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 9.5px; color: ${REPORT.secondary};">avoids streams · wetlands · 100-yr flood</div>
        </div>`;
      m.on('mousemove', 'cp-rect-line', (e) => { if (measureActiveRef.current) return; const f = e.features?.[0]; if (f) showLineSpecs(e.lngLat, cpRectHTML(f.properties || {})); });
      m.on('mouseleave', 'cp-rect-line', hideLineSpecs);

      // Stream hover — name, WOTUS class, what it means, miles on site, and
      // the AJD note on the two flagged drains.
      const streamHTML = (p: any) => {
        const mi = p.on_site === true || p.on_site === 'true'
          ? `${Number(p.mi_on_site).toFixed(2)} mi on Site ${esc(p.site)}`
          : `near Site ${esc(p.site)} (off Crown Pine land)`;
        const note = p.note
          ? `<div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 9px; color: ${REPORT.secondary}; margin-top: 3px; font-weight: 600;">${esc(p.note)}</div>`
          : '';
        const fdate = p.fdate
          ? `<div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 8.5px; color: ${REPORT.tertiary}; margin-top: 3px;">USGS last edit ${esc(p.fdate)} — classification derives from historical topo mapping; delineation governs</div>`
          : '';
        return `
        <div style="font-family: ${PORTFOLIO_TOKENS.sans}; font-size: 11px; line-height: 1.45; color: ${REPORT.body}; max-width: 240px;">
          <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 8.5px; letter-spacing: 0.06em; color: ${REPORT.tertiary}; text-transform: uppercase;">${esc(p.class)} stream · NHD</div>
          <div style="margin-top: 3px; color: ${REPORT.heading}; font-weight: 600;">${esc(p.name)}</div>
          <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 10px; color: ${REPORT.secondary};">${mi}</div>
          <div style="font-size: 10px; color: ${REPORT.secondary}; margin-top: 3px;">${esc(p.outlook)}</div>
          ${note}
          ${fdate}
        </div>`;
      };
      for (const lid of ['ss-per-line', 'ss-int-line']) {
        m.on('mousemove', lid, (e) => { if (measureActiveRef.current) return; const f = e.features?.[0]; if (f) showLineSpecs(e.lngLat, streamHTML(f.properties || {})); });
        m.on('mouseleave', lid, hideLineSpecs);
      }

      // FiberLight fiber hover — carrier + distance to Zavalla.
      const fiberHTML = (p: any) => `
        <div style="font-family: ${PORTFOLIO_TOKENS.sans}; font-size: 11px; line-height: 1.45; color: ${REPORT.body}; min-width: 160px;">
          <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 8.5px; letter-spacing: 0.06em; color: ${REPORT.tertiary}; text-transform: uppercase;">Fiber route</div>
          <div style="margin-top: 3px; color: ${REPORT.heading}; font-weight: 600;">${esc(p.carrier) || 'FiberLight'}</div>
          ${p.mi_to_zavalla != null && p.mi_to_zavalla !== '' ? `<div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 10px; color: ${REPORT.secondary};">${Number(p.mi_to_zavalla).toFixed(2)} mi to Zavalla</div>` : ''}
        </div>`;
      m.on('mousemove', 'ovl-fiber', (e) => { if (measureActiveRef.current) return; const f = e.features?.[0]; if (f) showLineSpecs(e.lngLat, fiberHTML(f.properties || {})); });
      m.on('mouseleave', 'ovl-fiber', hideLineSpecs);
    }
  }, [ownerParcels, pipelineFamilyStyling, powerPopups, lrmStreamsAvailable]);

  // 1. Mount the map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const token = getMapboxToken();
    if (!token) {
      console.warn('[CrownPineMap] No VITE_MAPBOX_TOKEN set — map disabled');
      return;
    }
    mapboxgl.accessToken = token;

    const initialBounds = cycleBbox || [-104, 25, -78, 41]; // continental US-ish fallback
    const m = new mapboxgl.Map({
      container: containerRef.current,
      style: STYLE_URLS.light,
      bounds: [
        [initialBounds[0], initialBounds[1]],
        [initialBounds[2], initialBounds[3]],
      ],
      fitBoundsOptions: { padding: 40, duration: 0 },
      attributionControl: false,
    });
    m.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    m.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-left');

    // Live lat/lng readout. Throttled to ~10 updates/sec so dragging across
    // the canvas can't drive a render per mousemove event. Registered on the
    // map created in this effect, so the m.remove() below tears it down.
    let lastCursorEmit = 0;
    m.on('mousemove', (e) => {
      const now = performance.now();
      if (now - lastCursorEmit < 100) return;
      lastCursorEmit = now;
      setCursorCoords({ lat: e.lngLat.lat, lng: e.lngLat.lng });
    });
    m.on('mouseout', () => setCursorCoords(null));

    m.on('load', () => {
      installLayers(m, 'light');
      setReady(true);
      setStyleEpoch(e => e + 1);
    });

    mapRef.current = m;
    return () => {
      if (popupRef.current) popupRef.current.remove();
      if (ownerPopupRef.current) ownerPopupRef.current.remove();
      m.remove();
      mapRef.current = null;
    };
    // Mount once. Bounds re-fit handled by the rows/bbox effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Style swap — when the user toggles satellite/light, setStyle wipes all
  // user-added sources/layers. We re-install them once the new style is
  // loaded then bump styleEpoch so every data-dependent effect re-fires and
  // repopulates its source / re-applies its paint.
  const prevStyleModeRef = useRef<MapStyleMode>('light');
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !ready) return;
    if (prevStyleModeRef.current === styleMode) return;
    prevStyleModeRef.current = styleMode;
    const onStyleLoad = () => {
      installLayers(m, styleMode);
      setStyleEpoch(e => e + 1);
    };
    m.once('style.load', onStyleLoad);
    m.setStyle(STYLE_URLS[styleMode]);
  }, [styleMode, ready, installLayers]);

  // 2a. Push centroids into the tracts layer when rows change OR after a
  //     style swap (styleEpoch bump). Source population only — camera fit is
  //     a separate effect so style swaps don't reset the user's camera.
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    const features: GeoJSON.Feature[] = rows
      .filter(r => r.centroid)
      .map(r => {
        const c = markerColor(r.knockouts.count);
        return {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [r.centroid!.lng, r.centroid!.lat] },
          properties: {
            parcel_id: r.parcel_id,
            rank: r.rank ?? '',
            state: r.state,
            county: r.county,
            site_label: r.site_label || '',
            grade: r.grade || '',
            mw: r.key_numbers.available_mw ?? '',
            acres: r.key_numbers.acres ?? '',
            gates: r.knockouts.list.length ? r.knockouts.list.map(k => k.toUpperCase()).join(' · ') : '',
            fill: c.fill,
            border: c.border,
            count: r.knockouts.count,
          },
        };
      });
    const src = m.getSource('portfolio-tracts') as mapboxgl.GeoJSONSource | undefined;
    if (src) src.setData({ type: 'FeatureCollection', features });
  }, [ready, rows, styleEpoch]);

  // 2a-shapes. Push footprint geometry into the outline + parcel sources.
  //   Each feature carries the row's knockout color so fills/lines match the
  //   dot semantics. Runs on rows change and after a style swap (styleEpoch).
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    const outlineFeatures: GeoJSON.Feature[] = [];
    const parcelFeatures: GeoJSON.Feature[] = [];
    const cleanPadFeatures: GeoJSON.Feature[] = [];
    for (const r of rows) {
      const c = markerColor(r.knockouts.count);
      const props = { parcel_id: r.parcel_id, fill: c.fill, border: c.border, count: r.knockouts.count };
      if (r.geometry) {
        outlineFeatures.push({ type: 'Feature', geometry: r.geometry, properties: props });
      }
      if (r.member_geometries) {
        for (const g of r.member_geometries) {
          parcelFeatures.push({ type: 'Feature', geometry: g, properties: props });
        }
      }
      // Integra: one clean-pad MultiPolygon per block. Crown Pine pad mode
      // instead draws one feature per pad (built below), so skip the block form.
      if (!pads && r.clean_pad_geometry) {
        cleanPadFeatures.push({ type: 'Feature', geometry: r.clean_pad_geometry, properties: { parcel_id: r.parcel_id } });
      }
    }
    // Crown Pine pad mode — one independent, individually-keyed feature per pad
    // so it can be clicked, hovered, highlighted, and flown-to on its own.
    // buildableHighlight instead explodes the pad's buildable pieces into that
    // same layer: every piece carries the parent pad_id, so one click still
    // selects the site and the highlight ring still rings the whole site.
    const placementFeatures: GeoJSON.Feature[] = [];
    if (pads) {
      for (const pad of pads) {
        const buildable = pad.shortlist?.buildable;
        // The effective basis only when the viewer asked for it AND this block
        // actually carries one (pre-v6 records have no `effective` key at all,
        // and the server nulls it when the payload doesn't validate) — every
        // other case falls back to the conservative screened pieces.
        const effectivePieces = showEffectiveBuildable ? buildable?.effective?.pieces?.features : null;
        const onEffective = !!effectivePieces?.length;
        const pieces = buildableHighlight ? (onEffective ? effectivePieces : buildable?.pieces?.features) : null;
        if (pieces?.length) {
          if (pad.geometry) {
            placementFeatures.push({ type: 'Feature', geometry: pad.geometry, properties: { pad_id: pad.id } });
          }
          pieces.forEach((piece, i) => {
            if (!piece.geometry) return;
            const ac = Number(piece.properties?.ac);
            cleanPadFeatures.push({
              type: 'Feature',
              geometry: piece.geometry,
              properties: {
                pad_id: pad.id,
                // Unique per piece — what the per-piece hover highlight keys on.
                piece_id: `${pad.id}#p${i}`,
                block_id: pad.shortlist?.block_id ?? '',
                flood_effective_date: pad.shortlist?.flood_basis?.effective_date ?? '',
                parcel_id: pad.parent?.parcel_id ?? '',
                acres: Number.isFinite(ac) ? ac : null,
                piece: 1,
                // Which basis this piece came from, and (effective only) how
                // many screened pieces it rejoined — the hover popup states
                // both so an effective shape is never read as screened ground.
                basis: onEffective ? 'effective' : 'screened',
                crossings: onEffective ? Number(piece.properties?.crossings) || 0 : 0,
                // No painted acreage: dozens of pieces stacked their numbers
                // into an unreadable pile. The hover popup carries them.
                label: '',
              },
            });
          });
          continue;
        }
        if (!pad.geometry) continue;
        cleanPadFeatures.push({
          type: 'Feature',
          geometry: pad.geometry,
          properties: {
            pad_id: pad.id,
            parcel_id: pad.parent?.parcel_id ?? '',
            acres: pad.acres ?? null,
            slope: pad.avg_slope_pct ?? null,
            shortlist: pad.shortlist ? 1 : 0,
            flood_effective_date: pad.shortlist?.flood_basis?.effective_date ?? '',
            label: pad.acres != null ? `${Math.round(pad.acres).toLocaleString()} ac` : '',
          },
        });
      }
    }
    const outlineSrc = m.getSource('portfolio-outlines') as mapboxgl.GeoJSONSource | undefined;
    if (outlineSrc) outlineSrc.setData({ type: 'FeatureCollection', features: outlineFeatures });
    const parcelSrc = m.getSource('portfolio-parcels') as mapboxgl.GeoJSONSource | undefined;
    if (parcelSrc) parcelSrc.setData({ type: 'FeatureCollection', features: parcelFeatures });
    const cleanPadSrc = m.getSource('portfolio-clean-pad') as mapboxgl.GeoJSONSource | undefined;
    if (cleanPadSrc) cleanPadSrc.setData({ type: 'FeatureCollection', features: cleanPadFeatures });
    const placementSrc = m.getSource('portfolio-campus-placement') as mapboxgl.GeoJSONSource | undefined;
    if (placementSrc) placementSrc.setData({ type: 'FeatureCollection', features: placementFeatures });
  }, [ready, rows, pads, buildableHighlight, showEffectiveBuildable, styleEpoch]);

  // Whether the current rows carry any member-parcel geometry at all. Several
  // callers (the Integra client map; the research page's LRM Shortlist view)
  // build rows with member_geometries: null, which made the Assemblies/Parcels
  // control switch between "outlines" and "outlines plus nothing".
  const hasMemberGeometries = rows.some(r => !!r.member_geometries?.length);

  // Rows can change under a map already in Parcels mode (view switch, tier
  // toggle). Fall back to Assemblies rather than stranding it in a mode whose
  // control is no longer rendered.
  useEffect(() => {
    if (!hasMemberGeometries && showParcels) setShowParcels(false);
  }, [hasMemberGeometries, showParcels]);

  // 2a-toggle. Show/hide the member-parcel layers when the user flips the
  //   Assemblies/Parcels control. styleEpoch reapplies after a style swap
  //   (which resets every layer to its installed default visibility).
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    const vis = showParcels ? 'visible' : 'none';
    for (const id of ['portfolio-parcels-fill', 'portfolio-parcels-line']) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', vis);
    }
  }, [showParcels, ready, styleEpoch]);

  // 2c-toggle. Show/hide the clean-pad layers. Mirrors the parcels toggle;
  // styleEpoch reapplies after a style swap (which resets installed defaults).
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    const vis = showCleanPad ? 'visible' : 'none';
    for (const id of ['portfolio-clean-pad-fill', 'portfolio-clean-pad-line', 'portfolio-clean-pad-hover-fill',
      'portfolio-clean-pad-hover-line', 'portfolio-clean-pad-active', 'portfolio-clean-pad-label']) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', vis);
    }
  }, [showCleanPad, ready, styleEpoch]);

  // 2c-placement. Show/hide the screened-placement outline (buildableHighlight
  // mode). Its source is empty everywhere else, so this is inert there.
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    if (m.getLayer('portfolio-campus-placement-line')) {
      m.setLayoutProperty('portfolio-campus-placement-line', 'visibility', showCampusPlacement ? 'visible' : 'none');
    }
  }, [showCampusPlacement, ready, styleEpoch]);

  // 2b. Fit to cycle bbox — fires only when cycleBbox itself changes, NOT on
  //     style swaps (a swap would otherwise jerk the user back from their
  //     current zoom/pan position).
  useEffect(() => {
    if (!ready || !mapRef.current || !cycleBbox) return;
    mapRef.current.fitBounds(
      [[cycleBbox[0], cycleBbox[1]], [cycleBbox[2], cycleBbox[3]]],
      { padding: 50, duration: 300 }
    );
  }, [ready, cycleBbox]);

  // 3. Fetch pipeline corridors for the cycle bbox. Re-fetches on style swap
  //    because setStyle wipes the source data along with the layers.
  useEffect(() => {
    if (!ready || !mapRef.current || !cycleBbox || styleEpoch === 0) return;
    const m = mapRef.current;
    // Fetch wider than the cycle bbox: screened supply corridors reach up to
    // ~6.5 mi past the outermost block (371 → NET SOUTH is 6.4 mi due north of
    // the cycle's top edge), and a corridor connector pointing at an unfetched
    // line renders as a dash into empty map. 0.12° ≈ 8 mi covers every primary.
    // Camera fit still uses the unpadded cycleBbox.
    const PIPELINE_FETCH_PAD_DEG = 0.12;
    const bbox = [
      cycleBbox[0] - PIPELINE_FETCH_PAD_DEG,
      cycleBbox[1] - PIPELINE_FETCH_PAD_DEG,
      cycleBbox[2] + PIPELINE_FETCH_PAD_DEG,
      cycleBbox[3] + PIPELINE_FETCH_PAD_DEG,
    ].join(',');
    // min_mw=400 thresholds out small laterals — at portfolio scale only the
    // corridors that could realistically anchor a 2,500-ac BTM site matter.
    // pipelineUnfiltered drops the floor entirely (rather than passing 0, which
    // the route turns into `mw_potential >= 0` and so still drops the segments
    // whose MW is unmodeled) so every screened ET/KM lateral draws.
    // zoom=8 picks a moderate simplification level (~500m tolerance).
    // Share-link viewers don't have a localStorage Bearer; route auth via
    // ?token= instead. portfolio_share_tokens-authed requests bypass simpleAuth
    // and are allowed on /api/pipelines/geojson via markPortfolioScopeAllowed.
    const mwFloor = pipelineUnfiltered ? '' : '&min_mw=400';
    const base = `/api/pipelines/geojson?capacity_semantics=2&bbox=${bbox}&zoom=8&group=gas_transmission${mwFloor}`;
    const url = shareToken ? `${base}&token=${encodeURIComponent(shareToken)}` : base;
    const headers: Record<string, string> = {};
    if (!shareToken) {
      const password = typeof window !== 'undefined' ? localStorage.getItem('pp-auth') : null;
      if (password) headers['Authorization'] = `Bearer ${password}`;
    }
    fetch(url, { headers })
      .then(r => r.ok ? r.json() : EMPTY_FC)
      .then((fc: GeoJSON.FeatureCollection) => {
        const src = m.getSource('portfolio-pipelines') as mapboxgl.GeoJSONSource | undefined;
        if (src) src.setData(fc);
      })
      .catch(err => console.warn('[CrownPineMap] pipelines fetch failed:', err));
  }, [ready, cycleBbox, styleEpoch, shareToken, pipelineUnfiltered]);

  // 3-b. Gas pipeline layer visibility. Stays visible unless a caller surfaces
  //      the Layers row and the viewer turns it off. The hit strip follows the
  //      drawn line, so a hidden pipeline layer stops answering hovers too.
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    for (const id of ['portfolio-pipelines', PIPELINE_HIT_LAYER]) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', showPipelines ? 'visible' : 'none');
    }
  }, [showPipelines, ready, styleEpoch]);

  // 3a. Screened connector for the active site. Geometry comes from the
  //     payload (no fetch); a site without segments simply clears the layers.
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const src = mapRef.current.getSource('portfolio-connector') as mapboxgl.GeoJSONSource | undefined;
    if (!src) return;
    const features: GeoJSON.Feature[] = [];
    if (connector?.cp) {
      features.push({ type: 'Feature', geometry: connector.cp, properties: { segment: 'cp', label: connector.cpLabel ?? '' } });
    }
    if (connector?.outside) {
      features.push({ type: 'Feature', geometry: connector.outside, properties: { segment: 'outside', label: connector.outsideLabel ?? '', detail: connector.outsideDetail ?? '' } });
    }
    src.setData({ type: 'FeatureCollection', features });
  }, [connector, ready, styleEpoch]);

  // Effective and pending FEMA are distinct sources under the same Flood control.
  // Fixed county bounds avoid refetching on pan; both requests retain share auth.
  const loadFlood = useCallback(() => {
    const m = mapRef.current;
    if (!m || !ready) return;
    const vis = showFlood ? 'visible' : 'none';
    const request = ++floodRequestRef.current;
    for (const id of ['portfolio-flood-fill', 'portfolio-flood-line', 'portfolio-pending-flood-fill', 'portfolio-pending-flood-line']) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', vis);
    }
    if (!showFlood) return;
    // Fetch the whole AOI once (fixed bbox), not the moving viewport — the county
    // clip already scopes it, so there's nothing to re-fetch as the user pans.
    const aoi = `&state=${FLOOD_AOI_STATE}&counties=${encodeURIComponent(FLOOD_AOI_COUNTIES.join(','))}`;
    const base = `/api/flood-zones/geojson?bbox=${FLOOD_AOI_BBOX}&zoom=10${aoi}&fema_release=2026-09-26`;
    const url = shareToken ? `${base}&token=${encodeURIComponent(shareToken)}` : base;
    const headers: Record<string, string> = {};
    if (!shareToken) {
      const password = typeof window !== 'undefined' ? localStorage.getItem('pp-auth') : null;
      if (password) headers['Authorization'] = `Bearer ${password}`;
    }
    fetch(url, { headers })
      .then(r => (r.ok ? r.json() : EMPTY_FC))
      .then((fc: GeoJSON.FeatureCollection & { truncated?: boolean }) => {
        if (request !== floodRequestRef.current || mapRef.current !== m) return;
        const src = m.getSource('portfolio-flood') as mapboxgl.GeoJSONSource | undefined;
        if (src) src.setData(fc);
        setFloodTruncated(previous => ({ ...previous, effective: fc.truncated === true }));
      })
      .catch(err => console.warn('[CrownPineMap] flood fetch failed:', err));
    const pendingUrl = url.replace('/flood-zones/geojson?', '/flood-zones/pending/geojson?');
    fetch(pendingUrl, { headers })
      .then(r => { if (!r.ok) throw new Error('Pending FEMA request failed'); return r.json(); })
      .then((fc: GeoJSON.FeatureCollection & { truncated?: boolean }) => {
        if (request !== floodRequestRef.current || mapRef.current !== m) return;
        const src = m.getSource('portfolio-pending-flood') as mapboxgl.GeoJSONSource | undefined;
        if (src) src.setData(fc);
        setPendingFloodLabels(pendingFemaLabels(fc));
        setFloodTruncated(previous => ({ ...previous, pending: fc.truncated === true }));
      })
      .catch(err => {
        if (request !== floodRequestRef.current || mapRef.current !== m) return;
        (m.getSource('portfolio-pending-flood') as mapboxgl.GeoJSONSource | undefined)?.setData(EMPTY_FC);
        setPendingFloodLabels([]);
        console.warn('[CrownPineMap] pending FEMA fetch failed:', err);
      });
  }, [ready, showFlood, shareToken]);

  // Flood is a fixed-AOI overlay: load once on toggle/style-swap, no moveend
  // refetch (that was the pan lag).
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    loadFlood();
  }, [showFlood, ready, styleEpoch, loadFlood]);

  // 3c. TWDB State Flood Plan existing 1% hazard — zoom-adaptive, two modes.
  //     Zoomed out it loads the whole cycle bbox at band 8: a 1.1km-tolerance
  //     county silhouette, fine as a screening cartoon but wildly wrong up
  //     close, where it breaks into spiky slivers nowhere near the real hazard
  //     edge. From z11 it follows the viewport and asks for the band matching
  //     the zoom instead — band 12 sits within a few percent of full-res truth.
  //     The zoom is capped at 14 because above that the endpoint leaves the
  //     precomputed bands for the full-res path and 400s on anything but a tiny
  //     bbox. Panning at low zoom does NOT refetch: the silhouette already
  //     covers the whole cycle, and that refetch is the pan lag the FEMA
  //     overlay above shed.
  const loadTwdbFlood = useCallback((force = false) => {
    const m = mapRef.current;
    if (!m || !ready) return;
    const vis = showTwdbFlood ? 'visible' : 'none';
    for (const id of ['portfolio-twdb-flood-fill', 'portfolio-twdb-flood-line']) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', vis);
    }
    if (!showTwdbFlood) return;
    const mode: 'aoi' | 'viewport' = m.getZoom() < TWDB_VIEWPORT_MIN_ZOOM ? 'aoi' : 'viewport';
    // A low-zoom pan re-asks for the silhouette already on the map — skip it.
    if (mode === 'aoi' && !force && twdbFloodModeRef.current === 'aoi') return;
    let bbox: string;
    let zoom: number;
    if (mode === 'aoi') {
      if (!cycleBbox) return;
      bbox = cycleBbox.join(',');
      zoom = 8;
    } else {
      const b = m.getBounds();
      bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()].join(',');
      zoom = Math.min(Math.round(m.getZoom()), TWDB_MAX_DISPLAY_ZOOM);
    }
    const base = `/api/twdb-flood-hazards/geojson?bbox=${bbox}&zoom=${zoom}`;
    const url = shareToken ? `${base}&token=${encodeURIComponent(shareToken)}` : base;
    const headers: Record<string, string> = {};
    if (!shareToken) {
      const password = typeof window !== 'undefined' ? localStorage.getItem('pp-auth') : null;
      if (password) headers['Authorization'] = `Bearer ${password}`;
    }
    const seq = ++twdbFloodSeqRef.current;
    fetch(url, { headers })
      .then(r => (r.ok ? r.json() : EMPTY_FC))
      .then((fc: GeoJSON.FeatureCollection) => {
        if (seq !== twdbFloodSeqRef.current) return;   // a newer viewport already won
        const src = m.getSource('portfolio-twdb-flood') as mapboxgl.GeoJSONSource | undefined;
        if (src) src.setData({ type: 'FeatureCollection', features: fc.features || [] });
        twdbFloodModeRef.current = mode;
      })
      .catch(err => console.warn('[CrownPineMap] TWDB flood fetch failed:', err));
  }, [ready, showTwdbFlood, cycleBbox, shareToken]);

  // Toggle and style swap force a full reload — a swap recreates the source, so
  // whatever band the mode ref remembers is no longer on the map. While the
  // layer is on, a debounced moveend keeps the viewport mode following the
  // bounds; the aoi mode skips inside the loader.
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    twdbFloodModeRef.current = null;
    loadTwdbFlood(true);
    if (!showTwdbFlood) return;
    let t: ReturnType<typeof setTimeout> | null = null;
    const onMove = () => {
      if (t) clearTimeout(t);
      t = setTimeout(() => { t = null; loadTwdbFlood(); }, 400);
    };
    m.on('moveend', onMove);
    return () => { if (t) clearTimeout(t); m.off('moveend', onMove); };
  }, [showTwdbFlood, ready, styleEpoch, loadTwdbFlood]);

  // 3d. County boundary lines — same pattern as the flood overlay: toggle
  //     visibility, and when on fetch county outlines for the current viewport.
  const loadCounties = useCallback(() => {
    const m = mapRef.current;
    if (!m || !ready) return;
    for (const id of ['portfolio-counties-line', 'portfolio-counties-label']) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', showCounties ? 'visible' : 'none');
    }
    if (!showCounties) return;
    const b = m.getBounds();
    const bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()].join(',');
    const z = Math.round(m.getZoom());
    const base = `/api/county-boundaries/geojson?bbox=${bbox}&zoom=${z}`;
    const url = shareToken ? `${base}&token=${encodeURIComponent(shareToken)}` : base;
    const headers: Record<string, string> = {};
    if (!shareToken) {
      const password = typeof window !== 'undefined' ? localStorage.getItem('pp-auth') : null;
      if (password) headers['Authorization'] = `Bearer ${password}`;
    }
    const seq = ++countiesSeqRef.current;
    fetch(url, { headers })
      .then(r => (r.ok ? r.json() : EMPTY_FC))
      .then((fc: GeoJSON.FeatureCollection) => {
        if (seq !== countiesSeqRef.current) return;   // a newer viewport already won
        const src = m.getSource('portfolio-counties') as mapboxgl.GeoJSONSource | undefined;
        if (src) src.setData(fc);
      })
      .catch(err => console.warn('[CrownPineMap] counties fetch failed:', err));
  }, [ready, showCounties, shareToken]);

  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    loadCounties();
    if (!showCounties) return;
    const onMove = () => loadCounties();
    m.on('moveend', onMove);
    return () => { m.off('moveend', onMove); };
  }, [showCounties, ready, styleEpoch, loadCounties]);

  // 3d. All-Crown-Pine owner parcels (research curation) — same viewport-fetch
  //     pattern as counties. The showSlope toggle repaints the same layer's fill
  //     between a flat tan and the slope choropleth (no refetch needed).
  const loadOwnerParcels = useCallback(() => {
    const m = mapRef.current;
    if (!m || !ready) return;
    const vis = showOwnerParcels ? 'visible' : 'none';
    for (const id of ['portfolio-owner-parcels-fill', 'portfolio-owner-parcels-line']) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', vis);
    }
    if (m.getLayer('portfolio-owner-parcels-fill')) {
      m.setPaintProperty('portfolio-owner-parcels-fill', 'fill-color',
        showSlope ? SLOPE_FILL : OWNER_PARCEL_COLOR);
    }
    if (!showOwnerParcels) return;
    const b = m.getBounds();
    const bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()].join(',');
    const z = Math.round(m.getZoom());
    const base = `/api/crownpine-parcels/geojson?bbox=${bbox}&zoom=${z}`;
    const url = shareToken ? `${base}&token=${encodeURIComponent(shareToken)}` : base;
    const headers: Record<string, string> = {};
    if (!shareToken) {
      const password = typeof window !== 'undefined' ? localStorage.getItem('pp-auth') : null;
      if (password) headers['Authorization'] = `Bearer ${password}`;
    }
    const seq = ++ownerParcelsSeqRef.current;
    fetch(url, { headers })
      .then(r => (r.ok ? r.json() : EMPTY_FC))
      .then((fc: GeoJSON.FeatureCollection) => {
        if (seq !== ownerParcelsSeqRef.current) return;   // a newer viewport already won
        const src = m.getSource('portfolio-owner-parcels') as mapboxgl.GeoJSONSource | undefined;
        if (src) src.setData(fc);
      })
      .catch(err => console.warn('[CrownPineMap] owner-parcels fetch failed:', err));
  }, [ready, showOwnerParcels, showSlope, shareToken]);

  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    loadOwnerParcels();
    if (!showOwnerParcels) return;
    const onMove = () => loadOwnerParcels();
    m.on('moveend', onMove);
    return () => { m.off('moveend', onMove); };
  }, [showOwnerParcels, showSlope, ready, styleEpoch, loadOwnerParcels]);

  // 3d-cad. County CAD parcel fabric — viewport-fetched like the owner parcels,
  //   but debounced (the fabric is heavy) and zoom-gated: below z13 the endpoint
  //   answers empty by design, so clear the layer and say why instead.
  const loadCadFabric = useCallback(() => {
    const m = mapRef.current;
    if (!m || !ready) return;
    const vis = showCadFabric ? 'visible' : 'none';
    for (const id of CAD_FABRIC_LAYER_IDS) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', vis);
    }
    const src = m.getSource('portfolio-cad-fabric') as mapboxgl.GeoJSONSource | undefined;
    if (!showCadFabric) { setCadHint(null); return; }
    if (m.getZoom() < CAD_MIN_ZOOM) {
      cadSeqRef.current++;               // strand any response still in flight
      src?.setData(EMPTY_FC);
      setCadHint('Zoom in to see county parcels');
      return;
    }
    const b = m.getBounds();
    const bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()].join(',');
    const z = Math.round(m.getZoom());
    const base = `/api/crownpine-parcels/cad?bbox=${bbox}&zoom=${z}`;
    const url = shareToken ? `${base}&token=${encodeURIComponent(shareToken)}` : base;
    const headers: Record<string, string> = {};
    if (!shareToken) {
      const password = typeof window !== 'undefined' ? localStorage.getItem('pp-auth') : null;
      if (password) headers['Authorization'] = `Bearer ${password}`;
    }
    const seq = ++cadSeqRef.current;
    fetch(url, { headers })
      .then(r => (r.ok ? r.json() : EMPTY_FC))
      .then((fc: GeoJSON.FeatureCollection & { truncated?: boolean }) => {
        if (seq !== cadSeqRef.current) return;   // a newer viewport already won
        src?.setData({ type: 'FeatureCollection', features: fc.features || [] });
        setCadHint(fc.truncated ? 'Largest parcels shown — zoom in' : null);
      })
      .catch(err => console.warn('[CrownPineMap] cad-fabric fetch failed:', err));
  }, [ready, showCadFabric, shareToken]);

  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    loadCadFabric();
    if (!showCadFabric) return;
    let t: ReturnType<typeof setTimeout> | null = null;
    const onMove = () => {
      if (t) clearTimeout(t);
      t = setTimeout(() => { t = null; loadCadFabric(); }, 400);
    };
    m.on('moveend', onMove);
    return () => { if (t) clearTimeout(t); m.off('moveend', onMove); };
  }, [showCadFabric, ready, styleEpoch, loadCadFabric]);

  // 3d-streambeds. Build-overable streambeds — viewport-fetched and debounced
  //   like the CAD fabric above. The endpoint takes NO zoom parameter and 400s
  //   on any bbox wider than 0.25° per axis, so the gate is a SPAN gate and it
  //   lives here: above that span the client never issues the request at all.
  const loadStreambeds = useCallback(() => {
    const m = mapRef.current;
    if (!m || !ready) return;
    if (m.getLayer(STREAMBED_LAYER_ID)) {
      m.setLayoutProperty(STREAMBED_LAYER_ID, 'visibility', showStreambeds ? 'visible' : 'none');
    }
    const src = m.getSource('portfolio-streambeds') as mapboxgl.GeoJSONSource | undefined;
    if (!showStreambeds) { setStreambedHint(null); return; }
    const b = m.getBounds();
    const [west, south, east, north] = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
    if (east - west > STREAMBED_MAX_SPAN_DEG || north - south > STREAMBED_MAX_SPAN_DEG) {
      streambedSeqRef.current++;         // strand any response still in flight
      src?.setData(EMPTY_FC);
      setStreambedHint('Zoom in to see streambeds');
      return;
    }
    const base = `/api/crownpine-parcels/streambeds?bbox=${[west, south, east, north].join(',')}`;
    const url = shareToken ? `${base}&token=${encodeURIComponent(shareToken)}` : base;
    const headers: Record<string, string> = {};
    if (!shareToken) {
      const password = typeof window !== 'undefined' ? localStorage.getItem('pp-auth') : null;
      if (password) headers['Authorization'] = `Bearer ${password}`;
    }
    const seq = ++streambedSeqRef.current;
    fetch(url, { headers })
      .then(r => (r.ok ? r.json() : EMPTY_FC))
      .then((fc: GeoJSON.FeatureCollection) => {
        if (seq !== streambedSeqRef.current) return;   // a newer viewport already won
        src?.setData({ type: 'FeatureCollection', features: fc.features || [] });
        setStreambedHint(null);
      })
      .catch(err => console.warn('[CrownPineMap] streambeds fetch failed:', err));
  }, [ready, showStreambeds, shareToken]);

  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    loadStreambeds();
    if (!showStreambeds) return;
    let t: ReturnType<typeof setTimeout> | null = null;
    const onMove = () => {
      if (t) clearTimeout(t);
      t = setTimeout(() => { t = null; loadStreambeds(); }, 400);
    };
    m.on('moveend', onMove);
    return () => { if (t) clearTimeout(t); m.off('moveend', onMove); };
  }, [showStreambeds, ready, styleEpoch, loadStreambeds]);

  // 3d-play. Pine Curtain play boundary — fixed geometry (one dissolved
  //   8-county polygon), so no moveend refetch: flip visibility, and fetch
  //   once when shown (re-fetched on styleEpoch after style swaps).
  const loadPlayBoundary = useCallback(() => {
    const m = mapRef.current;
    if (!m || !ready) return;
    const vis = showPlayBoundary ? 'visible' : 'none';
    for (const id of ['portfolio-play-boundary-fill', 'portfolio-play-boundary-line']) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', vis);
    }
    if (!showPlayBoundary) return;
    const base = '/api/play-boundary/geojson';
    const url = shareToken ? `${base}?token=${encodeURIComponent(shareToken)}` : base;
    const headers: Record<string, string> = {};
    if (!shareToken) {
      const password = typeof window !== 'undefined' ? localStorage.getItem('pp-auth') : null;
      if (password) headers['Authorization'] = `Bearer ${password}`;
    }
    fetch(url, { headers })
      .then(r => (r.ok ? r.json() : EMPTY_FC))
      .then((fc: GeoJSON.FeatureCollection) => {
        const src = m.getSource('portfolio-play-boundary') as mapboxgl.GeoJSONSource | undefined;
        if (src) src.setData(fc);
      })
      .catch(err => console.warn('[CrownPineMap] play-boundary fetch failed:', err));
  }, [ready, showPlayBoundary, shareToken]);

  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    loadPlayBoundary();
  }, [showPlayBoundary, ready, styleEpoch, loadPlayBoundary]);

  // 3d-rects. Crown Pine pad rectangles — fixed geometry, fetch once when
  //   shown (ON by default), styleEpoch refetch after style swaps.
  const loadCpRects = useCallback(() => {
    const m = mapRef.current;
    if (!m || !ready) return;
    const vis = showRects ? 'visible' : 'none';
    for (const id of CP_RECT_LAYER_IDS) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', vis);
    }
    if (!showRects) return;
    const base = '/api/crownpine-parcels/rects';
    const url = shareToken ? `${base}?token=${encodeURIComponent(shareToken)}` : base;
    const headers: Record<string, string> = {};
    if (!shareToken) {
      const password = typeof window !== 'undefined' ? localStorage.getItem('pp-auth') : null;
      if (password) headers['Authorization'] = `Bearer ${password}`;
    }
    fetch(url, { headers })
      .then(r => (r.ok ? r.json() : EMPTY_FC))
      .then((fc: GeoJSON.FeatureCollection) => {
        const src = m.getSource('portfolio-cp-rects') as mapboxgl.GeoJSONSource | undefined;
        if (src) src.setData(fc);
      })
      .catch(err => console.warn('[CrownPineMap] rects fetch failed:', err));
  }, [ready, showRects, shareToken]);

  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    loadCpRects();
  }, [showRects, ready, styleEpoch, loadCpRects]);

  // 3d-streams. Site A & C streams — fixed geometry, fetch once when shown.
  const loadSiteStreams = useCallback(() => {
    const m = mapRef.current;
    if (!m || !ready) return;
    const vis = showStreams ? 'visible' : 'none';
    for (const id of STREAM_LAYER_IDS) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', vis);
    }
    if (!showStreams) return;
    const base = '/api/crownpine-parcels/site-streams';
    const url = shareToken ? `${base}?token=${encodeURIComponent(shareToken)}` : base;
    const headers: Record<string, string> = {};
    if (!shareToken) {
      const password = typeof window !== 'undefined' ? localStorage.getItem('pp-auth') : null;
      if (password) headers['Authorization'] = `Bearer ${password}`;
    }
    fetch(url, { headers })
      .then(r => (r.ok ? r.json() : EMPTY_FC))
      .then((fc: GeoJSON.FeatureCollection) => {
        const src = m.getSource('portfolio-site-streams') as mapboxgl.GeoJSONSource | undefined;
        if (src) src.setData(fc);
      })
      .catch(err => console.warn('[CrownPineMap] site-streams fetch failed:', err));
  }, [ready, showStreams, shareToken]);

  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    loadSiteStreams();
  }, [showStreams, ready, styleEpoch, loadSiteStreams]);

  // 3d-lrm-streams. Client LRM streams — served whole, so it fetches once on
  //   the first toggle-on and replays from the ref cache afterwards (including
  //   after a style swap, which wipes the source). A failed fetch leaves the
  //   cache empty so a later toggle retries.
  useEffect(() => {
    if (!lrmStreamsAvailable || !ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    const vis = showLrmStreams ? 'visible' : 'none';
    for (const id of LRM_STREAM_LAYER_IDS) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', vis);
    }
    if (!showLrmStreams) return;
    const apply = (fc: GeoJSON.FeatureCollection) => {
      const src = m.getSource('portfolio-lrm-streams') as mapboxgl.GeoJSONSource | undefined;
      if (src) src.setData(fc);
    };
    if (lrmStreamsCacheRef.current) { apply(lrmStreamsCacheRef.current); return; }
    const base = '/api/crownpine-parcels/lrm-streams';
    const url = shareToken ? `${base}?token=${encodeURIComponent(shareToken)}` : base;
    const headers: Record<string, string> = {};
    if (!shareToken) {
      const password = typeof window !== 'undefined' ? localStorage.getItem('pp-auth') : null;
      if (password) headers['Authorization'] = `Bearer ${password}`;
    }
    fetch(url, { headers })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((fc: GeoJSON.FeatureCollection) => { lrmStreamsCacheRef.current = fc; apply(fc); })
      .catch(err => console.warn('[CrownPineMap] lrm-streams fetch failed:', err));
  }, [lrmStreamsAvailable, showLrmStreams, ready, styleEpoch, shareToken]);

  // 3d-a1-premises. A1 acquisition premises outline — one static, param-less
  //   FeatureCollection, so it follows the LRM-streams pattern exactly: fetch
  //   once on the first toggle-on, replay from the ref cache afterwards
  //   (including after a style swap, which wipes the source). No viewport
  //   following and no camera move — the outline appears where the site is.
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    const vis = showA1Premises ? 'visible' : 'none';
    for (const id of A1_PREMISES_LAYER_IDS) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', vis);
    }
    if (!showA1Premises) return;
    const apply = (fc: GeoJSON.FeatureCollection) => {
      const src = m.getSource('portfolio-a1-premises') as mapboxgl.GeoJSONSource | undefined;
      if (src) src.setData(fc);
    };
    if (a1PremisesCacheRef.current) { apply(a1PremisesCacheRef.current); return; }
    const base = '/api/crownpine-parcels/a1-premises';
    const url = shareToken ? `${base}?token=${encodeURIComponent(shareToken)}` : base;
    const headers: Record<string, string> = {};
    if (!shareToken) {
      const password = typeof window !== 'undefined' ? localStorage.getItem('pp-auth') : null;
      if (password) headers['Authorization'] = `Bearer ${password}`;
    }
    fetch(url, { headers })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((fc: GeoJSON.FeatureCollection) => { a1PremisesCacheRef.current = fc; apply(fc); })
      .catch(err => console.warn('[CrownPineMap] a1-premises fetch failed:', err));
  }, [showA1Premises, ready, styleEpoch, shareToken]);

  // 3d-oo. Other owners — fixed 13-site FeatureCollection (served whole,
  //   process-cached server-side), so no moveend refetch: flip visibility and
  //   fetch once when shown, mirroring the AOI loader.
  // 3e. Crown Pine LRM tracts — viewport-fetched like the owner parcels.
  const loadCpTracts = useCallback(() => {
    const m = mapRef.current;
    if (!m || !ready) return;
    const vis = showCpTracts ? 'visible' : 'none';
    for (const id of CP_TRACT_LAYER_IDS) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', vis);
    }
    if (!showCpTracts) return;
    const b = m.getBounds();
    const bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()].join(',');
    const z = Math.round(m.getZoom());
    const base = `/api/crownpine-parcels/tracts?bbox=${bbox}&zoom=${z}`;
    const url = shareToken ? `${base}&token=${encodeURIComponent(shareToken)}` : base;
    const headers: Record<string, string> = {};
    if (!shareToken) {
      const password = typeof window !== 'undefined' ? localStorage.getItem('pp-auth') : null;
      if (password) headers['Authorization'] = `Bearer ${password}`;
    }
    const seq = ++cpTractsSeqRef.current;
    fetch(url, { headers })
      .then(r => (r.ok ? r.json() : EMPTY_FC))
      .then((fc: GeoJSON.FeatureCollection) => {
        if (seq !== cpTractsSeqRef.current) return;   // a newer viewport already won
        const src = m.getSource('portfolio-cp-tracts') as mapboxgl.GeoJSONSource | undefined;
        if (src) src.setData(fc);
      })
      .catch(err => console.warn('[CrownPineMap] cp-tracts fetch failed:', err));
  }, [ready, showCpTracts, shareToken]);

  // Visibility flips synchronously; only the viewport fetch is debounced. The
  // mount fitBounds (effect 2b, 300 ms) used to land AFTER this loader's eager
  // call, so on the research page — where the layer defaults on — the tracts
  // fetched twice: once for the pre-fit viewport, then again for the real one
  // (~1.3 MB, and the loser of that race could arrive last, since this loader
  // has no seq guard). Routing the first load through the same 400 ms debounce
  // as moveend collapses the pair into one fetch for the settled viewport.
  // moveend is bound only while the layer is on, matching loadCounties.
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    const vis = showCpTracts ? 'visible' : 'none';
    for (const id of CP_TRACT_LAYER_IDS) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', vis);
    }
    if (!showCpTracts) return;
    let t: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (t) clearTimeout(t);
      t = setTimeout(() => { t = null; loadCpTracts(); }, 400);
    };
    schedule();
    m.on('moveend', schedule);
    return () => { if (t) clearTimeout(t); m.off('moveend', schedule); };
  }, [showCpTracts, ready, styleEpoch, loadCpTracts]);

  const loadOtherOwners = useCallback(() => {
    const m = mapRef.current;
    if (!m || !ready) return;
    const vis = showOtherOwners ? 'visible' : 'none';
    for (const id of OTHER_OWNER_LAYER_IDS) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', vis);
    }
    if (!showOtherOwners) return;
    const base = '/api/crownpine-parcels/other-owners';
    const url = shareToken ? `${base}?token=${encodeURIComponent(shareToken)}` : base;
    const headers: Record<string, string> = {};
    if (!shareToken) {
      const password = typeof window !== 'undefined' ? localStorage.getItem('pp-auth') : null;
      if (password) headers['Authorization'] = `Bearer ${password}`;
    }
    // cache: 'no-cache' forces revalidation against the server ETag on every
    // toggle. Without it a client that fetched under the old `max-age=86400`
    // header keeps that copy for a full day and never learns the layer changed
    // — exactly what hid the 2026-07-24 expansion from 13 tracts to 35.
    // Revalidation is cheap: an unchanged seed returns 304 with no body.
    fetch(url, { headers, cache: 'no-cache' })
      .then(r => (r.ok ? r.json() : EMPTY_FC))
      .then((fc: GeoJSON.FeatureCollection) => {
        const src = m.getSource('portfolio-other-owners') as mapboxgl.GeoJSONSource | undefined;
        if (src) src.setData(fc);
        // The layer now reaches well beyond the map's default frame — the
        // 2026-07-24 GW-campus tracts run from Hardin (~60 mi south) to Franklin
        // (~115 mi north), so without this the new tracts load off-screen and the
        // whole point of showing them is lost. Zoom out to the layer's own extent
        // when it is switched on.
        let b: [number, number, number, number] | null = null;
        for (const f of fc.features || []) {
          const fb = f.geometry ? geomBounds(f.geometry) : null;
          if (!fb) continue;
          b = b ? [Math.min(b[0], fb[0]), Math.min(b[1], fb[1]),
                   Math.max(b[2], fb[2]), Math.max(b[3], fb[3])] : fb;
        }
        if (b) m.fitBounds([[b[0], b[1]], [b[2], b[3]]], { padding: 60, duration: 800 });
      })
      .catch(err => console.warn('[CrownPineMap] other-owners fetch failed:', err));
  }, [ready, showOtherOwners, shareToken]);

  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    loadOtherOwners();
  }, [showOtherOwners, ready, styleEpoch, loadOtherOwners]);

  // 3e. Research overlays — one generic viewport loader for all four (habitat /
  //     transmission / substations / highways). Sets each layer's visibility and
  //     fetches the active ones for the current viewport (moveend refetch).
  const loadOverlays = useCallback(() => {
    const m = mapRef.current;
    if (!m || !ready) return;
    const b = m.getBounds();
    const bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()].join(',');
    const password = typeof window !== 'undefined' ? localStorage.getItem('pp-auth') : null;
    for (const o of MAP_OVERLAYS) {
      const on = overlays.has(o.key);
      for (const id of overlayLayerIds(o)) {
        if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none');
      }
      if (!on) continue;
      const base = o.url(bbox);
      const url = shareToken ? `${base}&token=${encodeURIComponent(shareToken)}` : base;
      const headers: Record<string, string> = {};
      if (!shareToken && password) headers['Authorization'] = `Bearer ${password}`;
      fetch(url, { headers })
        .then(r => (r.ok ? r.json() : EMPTY_FC))
        .then((fc: GeoJSON.FeatureCollection) => {
          const src = m.getSource(`ovl-${o.key}`) as mapboxgl.GeoJSONSource | undefined;
          if (src) src.setData(fc);
        })
        .catch(err => console.warn(`[CrownPineMap] ${o.key} fetch failed:`, err));
    }
  }, [ready, overlays, shareToken]);

  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    loadOverlays();
    if (overlays.size === 0) return;
    const onMove = () => loadOverlays();
    m.on('moveend', onMove);
    return () => { m.off('moveend', onMove); };
  }, [overlays, ready, styleEpoch, loadOverlays]);

  // 4. Highlight the hovered marker. styleEpoch reapplies the paint property
  //    on the freshly-installed dot layer after a style swap.
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    if (m.getLayer('portfolio-tracts-dot')) {
      m.setPaintProperty('portfolio-tracts-dot', 'circle-stroke-width', [
        'case',
        ['==', ['get', 'parcel_id'], hoverParcel || ''],
        2.4,
        1.2,
      ]);
    }
  }, [hoverParcel, ready, styleEpoch]);

  // 5. Externally-driven active selection — filters the accent-ring layer and
  //    zooms the map to the picked tract. Always zooms in (vs. the previous
  //    "stay put if in view" behavior) so the user sees the parcel in context
  //    even from a cold cycle-wide bbox. fitBounds with maxZoom keeps tiny
  //    parcels from getting cranked to street level.
  const prevActiveParcelIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    if (m.getLayer('portfolio-tracts-active-ring')) {
      m.setFilter('portfolio-tracts-active-ring', ['==', ['get', 'parcel_id'], activeParcelId || '']);
    }
    // Thicken the active site's outline to match the accent ring.
    if (m.getLayer('portfolio-outline-line')) {
      m.setPaintProperty('portfolio-outline-line', 'line-width', [
        'case',
        ['==', ['get', 'parcel_id'], activeParcelId || ''],
        2.8,
        1.4,
      ]);
    }
    // Crown Pine pad mode drives its own flyTo off the selected PAD (effect 5b),
    // so don't also fly to the parent block's bbox here — they'd fight.
    if (padModeRef.current) return;
    // Camera moves ONLY on a genuine selection change. The effect also re-runs
    // on styleEpoch bumps and rows-identity refreshes (they must re-apply the
    // ring filter above), and those re-runs must never touch the camera — a
    // layer toggle or Map/Aerial swap yanking the view back to the last-picked
    // site reads as the map fighting the user.
    if (prevActiveParcelIdRef.current === activeParcelId) return;
    prevActiveParcelIdRef.current = activeParcelId;
    if (!activeParcelId) return;
    const row = rows.find(r => r.parcel_id === activeParcelId);
    if (!row) return;
    if (row.bbox) {
      // Prefer bbox-based fitBounds — adapts zoom to actual parcel extent.
      // maxZoom 11 ≈ neighborhood detail with surrounding context still
      // visible; 1100ms ease feels deliberate rather than jarring.
      m.fitBounds(
        [[row.bbox[0], row.bbox[1]], [row.bbox[2], row.bbox[3]]],
        { padding: 100, maxZoom: 11, duration: 1100 }
      );
    } else if (row.centroid) {
      m.flyTo({ center: [row.centroid.lng, row.centroid.lat], zoom: 10, speed: 0.9, curve: 1.4 });
    }
  }, [activeParcelId, ready, rows, styleEpoch]);

  // 5b. Crown Pine pad HIGHLIGHT (no zoom) — ring the selected (or, failing
  //     that, hovered) pad. Selection deliberately does NOT move the map, so
  //     the user can scan the list without it lurching. Zoom is effect 5c.
  //     Hovering a piece ON THE MAP suppresses the whole-site ring (effect 5b-piece
  //     lights that one piece instead); hovering a LIST row still rings the site.
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    const highlightId = activePadId || (hoveredPieceId ? '' : hoveredPadId) || '';
    if (m.getLayer('portfolio-clean-pad-active')) {
      m.setFilter('portfolio-clean-pad-active', ['==', ['get', 'pad_id'], highlightId]);
    }
  }, [activePadId, hoveredPadId, hoveredPieceId, ready, styleEpoch]);

  // 5b-piece. The single buildable piece under the cursor.
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    for (const id of ['portfolio-clean-pad-hover-fill', 'portfolio-clean-pad-hover-line']) {
      if (m.getLayer(id)) m.setFilter(id, ['==', ['get', 'piece_id'], hoveredPieceId || '']);
    }
  }, [hoveredPieceId, ready, styleEpoch]);

  // 5b-move. Selecting a card MOVES the map to that pad (recenters; lifts to a
  //   regional zoom if still cycle-wide). Deliberately NOT keyed on hover, so
  //   scanning rows doesn't jerk the map. A tighter zoom-in is the side-panel
  //   action (effect 5c).
  const prevMovePadIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    // Move only on a genuine selection change — never when the effect re-runs
    // because pads refreshed or the basemap style swapped (see effect 5's note).
    if (prevMovePadIdRef.current === (activePadId ?? null)) return;
    prevMovePadIdRef.current = activePadId ?? null;
    if (!activePadId || !pads) return;
    const pad = pads.find(p => p.id === activePadId);
    if (!pad?.geometry) return;
    const b = geomBounds(pad.geometry);
    if (!b) return;
    mapRef.current.easeTo({
      center: [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2],
      zoom: Math.max(mapRef.current.getZoom(), 9),
      duration: 700,
    });
  }, [activePadId, ready, pads, styleEpoch]);

  // 5c. Crown Pine pad ZOOM — fires ONLY when a pad's side panel is opened
  //     (focusPadId), never on plain selection. Pads carry no server bbox, so
  //     bounds come from the geometry (geomBounds).
  const prevFocusPadIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    // Same transition gate as 5b-move: zoom on a new focus, not on re-runs.
    if (prevFocusPadIdRef.current === (focusPadId ?? null)) return;
    prevFocusPadIdRef.current = focusPadId ?? null;
    if (!focusPadId || !pads) return;
    const pad = pads.find(p => p.id === focusPadId);
    if (!pad?.geometry) return;
    const b = focusBoundsForPad(pad, { buildableHighlight, showEffectiveBuildable });
    if (b) {
      mapRef.current.fitBounds([[b[0], b[1]], [b[2], b[3]]], { padding: 90, maxZoom: 12.5, duration: 1000 });
    }
  }, [focusPadId, ready, pads, buildableHighlight, showEffectiveBuildable, styleEpoch]);

  const effectiveHeight = stuck ? stuckHeight : height;
  // Names the basis on the map's own row + legend, so the highlight is never
  // ambiguous once the viewer has switched away from the screened default.
  const padLayerLabel = buildableHighlight
    ? (showEffectiveBuildable ? 'Buildable acreage · effective' : 'Buildable acreage')
    : pads?.some(pad => pad.shortlist) ? 'Phase footprint' : 'Clean pad';

  // Client-map overlay rows, in the order the caller listed them. Each row is
  // "on" only when every overlay key it drives is on (Power drives two).
  const clientRows: LayerItem[] = clientOverlays
    .map(key => ({ key, row: CLIENT_OVERLAY_ROWS[key] }))
    .filter((r): r is { key: string; row: { label: string; color: string; keys: string[] } } => !!r.row)
    .map(({ key, row }) => {
      const active = row.keys.every(k => overlays.has(k));
      return { key, label: row.label, color: row.color, active, onToggle: () => setOverlayKeys(row.keys, !active) };
    });

  // Rows shared by both menu assemblies below.
  const floodRow = { key: 'flood', label: 'Flood', color: FLOOD_COLOR, active: showFlood, onToggle: () => setShowFlood(v => !v) };
  const twdbRow = { key: 'twdb-flood', label: 'TWDB Existing 1% Hazard', color: TWDB_FLOOD_COLOR, active: showTwdbFlood, onToggle: () => setShowTwdbFlood(v => !v) };
  const countiesRow = { key: 'counties', label: 'Counties', color: COUNTY_COLOR, active: showCounties, onToggle: () => setShowCounties(v => !v) };
  // Offered on both pages — the county fabric is how you see the inholdings
  // and third-party strips inside a block, whoever is looking.
  const cadFabricRow = { key: 'cadfabric', label: 'County parcels · owners', color: CAD_FABRIC_COLOR, active: showCadFabric, onToggle: () => setShowCadFabric(v => !v) };
  const cpTractsRow = { key: 'cptracts', label: 'Crown Pine tracts (LRM)', color: CP_TRACT_CPT1_COLOR, active: showCpTracts, onToggle: () => setShowCpTracts(v => !v) };
  const lrmStreamsRow = { key: 'lrmstreams', label: 'Streams', color: STREAM_PER_COLOR, active: showLrmStreams, onToggle: () => setShowLrmStreams(v => !v) };
  const pipelinesRow = { key: 'pipelines', label: 'Pipelines · ET & KM', color: ET_PIPELINE_COLOR, active: showPipelines, onToggle: () => setShowPipelines(v => !v) };
  const clientRow = (k: string) => clientRows.find(r => r.key === k);
  // Client feature keys placed explicitly; anything else the caller listed
  // still renders (under Features) instead of silently vanishing.
  const placedClientKeys = ['power', 'highways', 'railroads', 'airports', 'fiber', 'wells'];

  // Every data overlay, unified for the Layers menu. The research page
  // (ownerParcels) keeps its historical flat order; the client page groups
  // rows intentionally (Matthew 2026-09-01): screen story on top, then
  // Features, Parcel data, and the buildable Knockouts together at the bottom.
  const layerItems: LayerMenuEntry[] = ownerParcels ? [
    { key: 'cleanpad', label: padLayerLabel, color: CLEANPAD_COLOR, active: showCleanPad, onToggle: () => setShowCleanPad(v => !v) },
    // Offered on both pages — the premises is what is actually being acquired,
    // whoever is looking. Only its DEFAULT differs (see showA1Premises).
    { key: 'a1premises', label: 'A1 premises', color: REPORT.heading, active: showA1Premises, onToggle: () => setShowA1Premises(v => !v) },
    ...(hideResearchBaseLayers ? [] : [
      { key: 'rects', label: 'Pad rectangles', color: CP_RECT_COLOR, active: showRects, onToggle: () => setShowRects(v => !v) },
      { key: 'streams', label: 'Streams · Sites A&C', color: STREAM_PER_COLOR, active: showStreams, onToggle: () => setShowStreams(v => !v) },
    ]),
    ...(pipelineFamilyStyling ? [pipelinesRow] : []),
    ...(lrmStreamsAvailable ? [lrmStreamsRow] : []),
    // Research page only — lines with no basis to swap.
    { key: 'streambeds', label: 'Streambeds — build-overable', color: STREAM_INT_COLOR, active: showStreambeds, onToggle: () => setShowStreambeds(v => !v) },
    floodRow, twdbRow, countiesRow, cadFabricRow, cpTractsRow,
    {
      key: 'ownerparcels', label: 'All Crown Pine', color: OWNER_PARCEL_COLOR,
      active: showOwnerParcels, onToggle: () => setShowOwnerParcels(v => !v),
      sub: {
        label: 'Shade by slope', active: showSlope,
        // Turning slope on also switches the parcel layer on (it's a repaint of
        // that layer) so the toggle never looks broken with nothing to repaint.
        onToggle: () => { const v = !showSlope; setShowSlope(v); if (v) setShowOwnerParcels(true); },
      },
    },
    { key: 'playboundary', label: 'Play boundary', color: PLAY_BOUNDARY_COLOR, active: showPlayBoundary, onToggle: () => setShowPlayBoundary(v => !v) },
    { key: 'otherowners', label: 'Other owners', color: OTHER_TRACT_COLOR, active: showOtherOwners, onToggle: () => setShowOtherOwners(v => !v) },
    ...RESEARCH_OVERLAYS.map(o => ({
      key: o.key, label: o.label, color: o.color, active: overlays.has(o.key), onToggle: () => toggleOverlay(o.key),
    })),
  ] : [
    { key: 'cleanpad', label: padLayerLabel, color: CLEANPAD_COLOR, active: showCleanPad, onToggle: () => setShowCleanPad(v => !v) },
    ...(buildableHighlight ? [
      // Sits directly under the buildable row it re-bases. ONE row does both
      // halves of the same idea: the highlight drops to the screened pieces
      // and the streambeds that severed them are drawn in the gaps it opens.
      { key: 'streambeds', label: 'Stream cuts · screened basis', color: STREAM_INT_COLOR, active: showStreambeds, onToggle: () => setShowStreambeds(v => !v) },
      // 'Screened placement' row removed from the client menu (Matthew
      // 2026-09-01 — "feels limiting"). The layer machinery stays; re-add the
      // row here to bring it back.
    ] : [
      { key: 'streambeds', label: 'Streambeds — build-overable', color: STREAM_INT_COLOR, active: showStreambeds, onToggle: () => setShowStreambeds(v => !v) },
    ]),
    { key: 'a1premises', label: 'A1 premises', color: REPORT.heading, active: showA1Premises, onToggle: () => setShowA1Premises(v => !v) },
    { heading: 'Features' },
    ...(pipelineFamilyStyling ? [pipelinesRow] : []),
    ...['power', 'highways', 'railroads', 'airports', 'fiber'].map(clientRow).filter((r): r is LayerItem => !!r),
    ...clientRows.filter(r => !placedClientKeys.includes(r.key)),
    { heading: 'Parcel data' },
    ...(cpTractsAvailable ? [cpTractsRow] : []),
    cadFabricRow, countiesRow,
    { heading: 'Knockouts' },
    floodRow,
    ...(lrmStreamsAvailable ? [lrmStreamsRow] : []),
    twdbRow,
    ...(clientRow('wells') ? [clientRow('wells') as LayerItem] : []),
  ];
  const activeLayerCount = layerItems.filter(i => !isHeading(i) && i.active).length;

  return (
    <>
      {/* Sentinel for IntersectionObserver — sits at the map's natural
          position; when it leaves the viewport upward, the map locks. */}
      {sticky && <div ref={sentinelRef} style={{ height: 1, marginTop: 24 }} />}
      <section style={{
        position: sticky ? 'sticky' : 'static',
        top: 0,
        zIndex: 20,
        marginBottom: 0,
        border: `1px solid ${REPORT.rule}`,
        borderRadius: 0,
        overflow: 'hidden',
        background: REPORT.white,
        boxShadow: stuck ? '0 6px 14px -8px rgba(46,56,64,0.22)' : 'none',
        transition: 'box-shadow 180ms ease',
      }}>
        <header style={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          padding: stuck ? '6px 14px' : '10px 14px',
          borderBottom: `1px solid ${REPORT.rule}`,
          background: REPORT.surface,
          transition: 'padding 180ms ease',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, rowGap: 6, flexWrap: 'wrap' }}>
            <div style={{
              fontFamily: PORTFOLIO_TOKENS.mono,
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
              color: REPORT.heading,
            }}>
              Portfolio Map{stuck ? ' · sticky' : ''}
            </div>
            {/* Modes / tools stay as pills; every data overlay lives in the
                Layers menu so the toolbar stops growing as layers are added. */}
            <StyleToggle mode={styleMode} onChange={setStyleMode} />
            {hasMemberGeometries && <ShapeToggle showParcels={showParcels} onChange={setShowParcels} />}
            {ownerParcels && (
              <>
                <PillToggle label="Measure" color="#ef4444" active={measureActive} onChange={armMeasure} />
                <PillToggle label="Area" color="#ef4444" active={areaActive} onChange={armArea} />
                {measureActive && measure.count > 0 && (
                  <button
                    type="button"
                    onClick={measure.clearAll}
                    style={{
                      fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.5, fontWeight: 700,
                      letterSpacing: '0.10em', textTransform: 'uppercase', padding: '4px 9px',
                      cursor: 'pointer', background: REPORT.white, color: REPORT.secondary,
                      border: `1px solid ${PORTFOLIO_TOKENS.ruleMid}`, borderRadius: 3,
                    }}
                  >
                    Clear ({measure.count})
                  </button>
                )}
              </>
            )}
            <LayersMenu items={layerItems} count={activeLayerCount} />
          </div>
        </header>
        {/* Legend — its OWN row below the toggles so appearing/disappearing
            items never shift the button positions above. */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 14,
          rowGap: 3,
          padding: '5px 14px',
          borderBottom: `1px solid ${REPORT.rule}`,
          background: REPORT.white,
          fontFamily: PORTFOLIO_TOKENS.mono,
          fontSize: 9,
          letterSpacing: '0.04em',
          color: REPORT.secondary,
        }}>
          {tierMarkerLegend ? (
            <>
              <LegendDot color={toneStyle('pass').fg} label="Recommended" />
              <LegendDot color={toneStyle('caution').fg} label="Conditional · Queue" />
            </>
          ) : (
            <>
              <LegendDot color={toneStyle('pass').fg} label="Clean" />
              <LegendDot color={toneStyle('caution').fg} label="1 gate" />
              <LegendDot color={toneStyle('fail').fg} label="≥2 gates" />
            </>
          )}
          {showCleanPad && <LegendDot color={CLEANPAD_COLOR} label={padLayerLabel} />}
          {buildableHighlight && showEffectiveBuildable && <span style={{ color: REPORT.tertiary }}>Effective basis: screened pieces rejoined across mapped intermittent streambeds (NWI R4), streambed acreage deducted.</span>}
          {buildableHighlight && showCampusPlacement && <LegendDot color={CP_RECT_COLOR} label="Screened placement" />}
          {/* Ink dot, not the satellite haze the line switches to — the legend
              sits on the page chrome, where haze on white would vanish. */}
          {showA1Premises && <LegendDot color={REPORT.heading} label={A1_PREMISES_LEGEND} />}
          {showRects && <LegendDot color={CP_RECT_COLOR} label="Pad rectangle" />}
          {showStreams && <LegendDot color={STREAM_PER_COLOR} label="Perennial" />}
          {showStreams && <LegendDot color={STREAM_INT_COLOR} label="Intermittent" />}
          {lrmStreamsAvailable && showLrmStreams && <LegendDot color={STREAM_PER_COLOR} label="Perennial" />}
          {lrmStreamsAvailable && showLrmStreams && <LegendDot color={STREAM_INT_COLOR} label="Intermittent" />}
          {showStreambeds && <LegendDot color={STREAM_INT_COLOR} label="Streambed — build-overable (NWI R4)" />}
          {showStreambeds && streambedHint && <span style={{ color: REPORT.tertiary }}>{streambedHint}</span>}
          {showFlood && <LegendDot color={FLOOD_COLOR} label="Effective FEMA · 1% annual chance" />}
          {showFlood && <LegendDot color="#f97316" label="Effective FEMA · 0.2% annual chance" />}
          {showFlood && pendingFloodLabels.map(label => <LegendDot key={label} color={PENDING_FEMA_COLOR} label={label} />)}
          {showFlood && (floodTruncated.effective || floodTruncated.pending) && <span style={{ color: REPORT.tertiary }}>FEMA overlay is incomplete at this extent.</span>}
          {showTwdbFlood && <LegendDot color={TWDB_FLOOD_COLOR} label="TWDB existing 1% annual-chance hazard" />}
          {showTwdbFlood && <span style={{ color: REPORT.tertiary }}>Targeted coverage: Frio + 20 Crown Pine ETX counties. TWDB planning compilation; may include modeled and regulatory source data; not itself a FEMA flood-zone determination.</span>}
          {showCounties && <LegendDot color={COUNTY_COLOR} label="County line" />}
          {showCadFabric && <LegendDot color={CAD_FABRIC_COLOR} label="County parcels · owners" />}
          {showCadFabric && cadHint && <span style={{ color: REPORT.tertiary }}>{cadHint}</span>}
          {showOwnerParcels && !showSlope && <LegendDot color={OWNER_PARCEL_COLOR} label="All Crown Pine" />}
          {showOwnerParcels && showSlope && <span>Slope: <span style={{ color: '#15803d' }}>flat</span> → <span style={{ color: '#b91c1c' }}>steep</span></span>}
          {pipelineFamilyStyling && showPipelines && <LegendDot color={ET_PIPELINE_COLOR} label="Energy Transfer" />}
          {pipelineFamilyStyling && showPipelines && <LegendDot color={KM_PIPELINE_COLOR} label="Kinder Morgan" />}
          {connector?.cp && <LegendDot color={CONNECTOR_CP_COLOR} label="Connector · on supplied inventory" />}
          {connector?.outside && <LegendDot color={CONNECTOR_OUTSIDE_COLOR} label="Connector · outside supplied inventory" />}
          {showPlayBoundary && <LegendDot color={PLAY_BOUNDARY_COLOR} label="Play boundary" />}
          {showOtherOwners && <LegendDot color={OTHER_TRACT_COLOR} label="Other owners" />}
          {showOtherOwners && <LegendDot color={OTHER_PAD_COLOR} label="Their pads" />}
          {showOtherOwners && <LegendDot color={OTHER_RECT_COLOR} label="500-ac rect" />}
          {(ownerParcels ? RESEARCH_OVERLAYS.filter(o => overlays.has(o.key)) : clientRows.filter(r => r.active)).map(o => (
            <LegendDot key={o.key} color={o.color} label={o.label} />
          ))}
          {measureActive && (
            <span style={{ color: '#ef4444', fontWeight: 600 }}>Measuring — click to add points · dbl-click / Esc to finish</span>
          )}
          {areaActive && (
            <span style={{ color: '#ef4444', fontWeight: 600 }}>Area — drag a rectangle for acreage · Esc clears</span>
          )}
          {!stuck && showPipelines && (
            <>
              <span style={{ color: REPORT.tertiary }}>·</span>
              <span>
                {pipelineUnfiltered
                  ? 'Gas transmission lines · screened operator families only'
                  : pipelineFamilyStyling
                    ? 'Gas transmission lines ≥400 MW · screened operator families only'
                    : 'Gas transmission lines ≥400 MW'}
              </span>
            </>
          )}
        </div>
        <div style={{ position: 'relative' }}>
          <div
            ref={containerRef}
            style={{
              width: '100%',
              height: effectiveHeight,
              transition: 'height 180ms ease',
            }}
          />
          {/* Quick-access cluster, overlaid upper-left on the canvas — the one
              free corner (Mapbox nav sits top-right, attribution + logo bottom-
              left). Each button flips the SAME state its Layers-menu row uses,
              so menu and cluster stay in sync automatically. Crown Pine
              research mode only: the Integra portfolio map (which never passes
              ownerParcels) keeps its exact prior rendering. */}
          {ownerParcels && (
            <div style={{
              position: 'absolute', top: 10, left: 10, zIndex: 10,
              display: 'flex', flexDirection: 'column', gap: 4,
            }}>
              <MapQuickButton title="County lines" label="Counties" glyph="⬚" color={COUNTY_COLOR} active={showCounties} onClick={() => setShowCounties(v => !v)} />
              <MapQuickButton title="Play boundary" label="Boundary" glyph="⬡" color={PLAY_BOUNDARY_COLOR} active={showPlayBoundary} onClick={() => setShowPlayBoundary(v => !v)} />
              <MapQuickButton title="Pad rectangles" label="Rectangles" glyph="▭" color={CP_RECT_COLOR} active={showRects} onClick={() => setShowRects(v => !v)} />
              <MapQuickButton title={padLayerLabel} label={padLayerLabel} glyph="■" color={CLEANPAD_COLOR} active={showCleanPad} onClick={() => setShowCleanPad(v => !v)} />
              {/* Tools, not layers — small gap sets them apart from the toggles. */}
              <div style={{ marginTop: 2, display: 'flex' }}>
                <MapQuickButton title="Measure distance" label="Measure" glyph="⤢" color="#ef4444" active={measureActive} onClick={() => armMeasure(!measureActive)} />
              </div>
              <MapQuickButton title="Measure area — drag a rectangle" label="Area" glyph="▭" color="#ef4444" active={areaActive} onClick={() => armArea(!areaActive)} />
            </div>
          )}
          {/* Measure tool for callers without the research quick cluster —
              tucked directly under Mapbox's zoom controls (top-right, ~60px
              tall at a 10px inset) so it reads as map chrome, not a data row. */}
          {measureControl && !ownerParcels && (
            <div style={{
              position: 'absolute', top: 76, right: 10, zIndex: 10,
              display: 'flex', flexDirection: 'column', gap: 4,
            }}>
              <MapQuickButton title="Measure distance" label="Measure" glyph="⤢" color="#ef4444" active={measureActive} onClick={() => armMeasure(!measureActive)} />
              <MapQuickButton title="Measure area — drag a rectangle" label="Area" glyph="▭" color="#ef4444" active={areaActive} onClick={() => armArea(!areaActive)} />
              {measureActive && measure.count > 0 && (
                <MapQuickButton title="Clear measurements" label={`Clear (${measure.count})`} glyph="✕" color={REPORT.secondary} active={false} onClick={measure.clearAll} />
              )}
            </div>
          )}
          {/* Cursor lat/lng — formatted to paste straight into Google Maps.
              Bottom-left, stacked ABOVE Mapbox's logo + compact attribution
              (which own the bottom ~33px of that corner and must stay
              unobstructed). It's the one edge free in BOTH page modes: the
              research map fills top-left with the quick cluster, and the
              client map puts its measure control under the top-right zoom
              buttons. Never intercepts the pointer. */}
          {cursorCoords && (
            <div style={{
              position: 'absolute', bottom: 38, left: 10, zIndex: 10,
              pointerEvents: 'none',
              fontFamily: PORTFOLIO_TOKENS.mono,
              fontSize: 10,
              lineHeight: 1.4,
              letterSpacing: '0.02em',
              color: REPORT.secondary,
              background: 'rgba(255,255,255,0.78)',
              border: `1px solid ${PORTFOLIO_TOKENS.ruleMid}`,
              borderRadius: 3,
              padding: '2px 6px',
            }}>
              {cursorCoords.lat.toFixed(5)}, {cursorCoords.lng.toFixed(5)}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

const LegendDot: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
    <span style={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: 4,
      background: color,
      boxShadow: '0 0 0 1px #ffffff',
    }} />
    {label}
  </span>
);

// Segmented control — base style switch. Active state uses REPORT.heading for
// strong selection contrast; inactive is ghost border so the control reads as
// optional chrome rather than a primary action.
const StyleToggle: React.FC<{
  mode: MapStyleMode;
  onChange: (m: MapStyleMode) => void;
}> = ({ mode, onChange }) => {
  const btn = (target: MapStyleMode, position: 'left' | 'right'): React.CSSProperties => ({
    fontFamily: PORTFOLIO_TOKENS.mono,
    fontSize: 8.5,
    fontWeight: 700,
    letterSpacing: '0.10em',
    textTransform: 'uppercase',
    padding: '4px 9px',
    cursor: 'pointer',
    background: mode === target ? REPORT.heading : REPORT.white,
    color: mode === target ? REPORT.white : REPORT.secondary,
    border: `1px solid ${mode === target ? REPORT.heading : PORTFOLIO_TOKENS.ruleMid}`,
    borderRadius: position === 'left' ? '3px 0 0 3px' : '0 3px 3px 0',
    marginRight: position === 'left' ? -1 : 0,
    transition: 'all 0.12s ease',
  });
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
      <button type="button" style={btn('light', 'left')} onClick={() => onChange('light')}>Map</button>
      <button type="button" style={btn('satellite', 'right')} onClick={() => onChange('satellite')}>Aerial</button>
    </div>
  );
};

// Segmented control — footprint detail. "Assemblies" shows the dissolved site
// outlines; "Parcels" additionally reveals the member-parcel subdivisions.
// Same visual grammar as StyleToggle so the two controls read as a set.
const ShapeToggle: React.FC<{
  showParcels: boolean;
  onChange: (v: boolean) => void;
}> = ({ showParcels, onChange }) => {
  const btn = (active: boolean, position: 'left' | 'right'): React.CSSProperties => ({
    fontFamily: PORTFOLIO_TOKENS.mono,
    fontSize: 8.5,
    fontWeight: 700,
    letterSpacing: '0.10em',
    textTransform: 'uppercase',
    padding: '4px 9px',
    cursor: 'pointer',
    background: active ? REPORT.heading : REPORT.white,
    color: active ? REPORT.white : REPORT.secondary,
    border: `1px solid ${active ? REPORT.heading : PORTFOLIO_TOKENS.ruleMid}`,
    borderRadius: position === 'left' ? '3px 0 0 3px' : '0 3px 3px 0',
    marginRight: position === 'left' ? -1 : 0,
    transition: 'all 0.12s ease',
  });
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
      <button type="button" style={btn(!showParcels, 'left')} onClick={() => onChange(false)}>Assemblies</button>
      <button type="button" style={btn(showParcels, 'right')} onClick={() => onChange(true)}>Parcels</button>
    </div>
  );
};

// Layers menu — a single dropdown holding every data overlay, so the toolbar
// stops accreting pills as layers are added. The trigger shows the active count;
// the panel is a checklist with a color swatch per layer. Closes on outside
// click or Escape. Sits inside the map section (overflow-visible over the map).
const LayersMenu: React.FC<{ items: LayerMenuEntry[]; count: number }> = ({ items, count }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const active = count > 0;
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.5, fontWeight: 700,
          letterSpacing: '0.10em', textTransform: 'uppercase', padding: '4px 9px',
          cursor: 'pointer',
          background: active ? REPORT.heading : REPORT.white,
          color: active ? REPORT.white : REPORT.secondary,
          border: `1px solid ${active ? REPORT.heading : PORTFOLIO_TOKENS.ruleMid}`,
          borderRadius: 3, transition: 'all 0.12s ease',
          display: 'inline-flex', alignItems: 'center', gap: 5,
        }}
      >
        Layers{count > 0 ? ` (${count})` : ''}
        <span style={{ fontSize: 7, opacity: 0.8 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 40,
            background: REPORT.white, border: `1px solid ${PORTFOLIO_TOKENS.ruleMid}`,
            borderRadius: 4, boxShadow: '0 8px 20px -8px rgba(46,56,64,0.30)',
            padding: '5px 0', minWidth: 196, maxHeight: 340, overflowY: 'auto',
          }}
        >
          {items.map(item => isHeading(item) ? (
            <div
              key={`h-${item.heading}`}
              style={{
                fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 7.5, fontWeight: 700,
                letterSpacing: '0.14em', textTransform: 'uppercase',
                color: REPORT.secondary, padding: '7px 12px 3px',
                borderTop: `1px solid ${PORTFOLIO_TOKENS.ruleMid}`, marginTop: 4,
              }}
            >
              {item.heading}
            </div>
          ) : (
            <React.Fragment key={item.key}>
              <LayerRow item={item} />
              {item.sub && item.active && (
                <LayerRow item={{ ...item.sub, key: `${item.key}-sub`, color: item.color }} indent />
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

// A single checklist row. The swatch doubles as the checkbox: filled with the
// layer's color (✓) when on, hollow outline when off. `indent` styles the
// nested shade-by-slope sub-row.
const LayerRow: React.FC<{
  item: { key?: string; label: string; color: string; active: boolean; onToggle: () => void };
  indent?: boolean;
}> = ({ item, indent }) => (
  <button
    type="button"
    role="menuitemcheckbox"
    aria-checked={item.active}
    onClick={item.onToggle}
    onMouseEnter={e => { e.currentTarget.style.background = REPORT.surface; }}
    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    style={{
      display: 'flex', alignItems: 'center', gap: 8, width: '100%',
      padding: indent ? '4px 12px 4px 28px' : '5px 12px',
      background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
      fontFamily: PORTFOLIO_TOKENS.mono, fontSize: indent ? 8.5 : 9.5,
      letterSpacing: '0.04em', textTransform: 'uppercase',
      color: item.active ? REPORT.heading : REPORT.secondary,
    }}
  >
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 12, height: 12, flexShrink: 0, borderRadius: 3,
      border: `1.5px solid ${item.color}`,
      background: item.active ? item.color : 'transparent',
      color: '#ffffff', fontSize: 9, lineHeight: 1,
    }}>
      {item.active ? '✓' : ''}
    </span>
    {item.label}
  </button>
);

// Generic on/off pill — Crown Pine research toggles (All CP, Slope). Active state
// fills with the passed color so the control states what it shows. `disabled`
// greys it out (Slope is meaningless until All CP is on).
const PillToggle: React.FC<{
  label: string;
  color: string;
  active: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}> = ({ label, color, active, onChange, disabled = false }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!active)}
    aria-pressed={active}
    disabled={disabled}
    style={{
      fontFamily: PORTFOLIO_TOKENS.mono,
      fontSize: 8.5,
      fontWeight: 700,
      letterSpacing: '0.10em',
      textTransform: 'uppercase',
      padding: '4px 9px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.4 : 1,
      background: active ? color : REPORT.white,
      color: active ? REPORT.white : REPORT.secondary,
      border: `1px solid ${active ? color : PORTFOLIO_TOKENS.ruleMid}`,
      borderRadius: 3,
      transition: 'all 0.12s ease',
    }}
  >
    {label}
  </button>
);

// Compact labeled chip for the on-map quick-access cluster. Same visual
// grammar as PillToggle (active fills with the layer's color, inactive is
// ghost border): glyph + short label, left-aligned, one chip per row. The
// column container stretches every chip to the widest label so the cluster
// reads as one control. Glyphs are plain unicode, matching the file's
// existing ▲▼✓ approach — no icon library.
const MapQuickButton: React.FC<{
  title: string;
  label: string;
  glyph: string;
  color: string;
  active: boolean;
  onClick: () => void;
}> = ({ title, label, glyph, color, active, onClick }) => (
  <button
    type="button"
    title={title}
    aria-pressed={active}
    onClick={onClick}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: 6,
      width: '100%',
      padding: '5px 10px 5px 8px',
      fontFamily: PORTFOLIO_TOKENS.mono,
      fontSize: 9.5,
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      cursor: 'pointer',
      background: active ? color : REPORT.white,
      color: active ? '#ffffff' : REPORT.secondary,
      border: `1px solid ${active ? color : PORTFOLIO_TOKENS.ruleMid}`,
      borderRadius: 4,
      boxShadow: '0 1px 4px rgba(46,56,64,0.20)',
      transition: 'all 0.12s ease',
    }}
  >
    <span style={{ fontSize: 12, lineHeight: 1 }}>{glyph}</span>
    {label}
  </button>
);

export default CrownPineMap;
